import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useRoutableDialog } from '../hooks/useRoutableDialog';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import FullScreenPage from '../components/FullScreenPage';
import { haptics } from '../utils/haptics';
import PinchZoomImage from '../components/PinchZoomImage';
import { requestPushPrompt } from '../components/PushPrompt';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import {
  Heart, MessageCircle, Dumbbell, Clock, Trophy, Flame,
  Send, ChevronDown, ChevronUp, Trash2, Loader2, Users, Check, X, MoreVertical, Bell, BellOff,
  ChevronLeft, ChevronRight, Apple, Beef, Wheat, Droplets, Share2, Eye, Scale, Pencil, Camera, Filter, Bookmark, Plus, Play, Edit2, Flag
} from 'lucide-react';
import client from '../api/client';
import ExerciseDetailModal from '../components/ExerciseDetailModal';
import StaticExerciseImage from '../components/StaticExerciseImage';import { useTranslation } from 'react-i18next';
import MuscleBodyMap from '../components/MuscleBodyMap';
import { compressImage } from '../utils/compressImage';
import { useSwipe } from '../hooks/useSwipe';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { PullToRefreshIndicator } from '../components/PullToRefreshIndicator';
import { TARGET_MUSCLE_COLORS, displayMuscleName, TARGET_TO_GROUP, MUSCLE_GROUP_COLORS } from '../lib/muscleUtils';
import { translateMuscle } from '../lib/muscleTranslations';
import { exerciseName, getExerciseInputMode, formatDuration, formatCardioSetSummary } from '../lib/exerciseDisplay';
import { useLanguage } from '../contexts/LanguageContext';
import ImageCropper from '../components/ImageCropper';
import LazyImage from '../components/LazyImage';
import { cloudinaryThumb } from '../utils/cloudinaryUpload';
import WorkoutStoryGenerator from '../components/WorkoutStoryGenerator';
import GenericPostStoryGenerator from '../components/GenericPostStoryGenerator';
import WorkoutEditDialog from '../components/WorkoutEditDialog';
import { SET_TYPES } from '../components/workout/sharedSetTypes';
import { formatDate } from '../lib/dateUtils';
import SubscriptionBadge from '../components/SubscriptionBadge';
import FollowSuggestionsCard from '../components/social/FollowSuggestionsCard';
import TierAvatar from '../components/leveling/TierAvatar';
import { getDisplayName, getDisplayInitials } from '../lib/displayName';
import { formatAmountUnit } from '../lib/formatAmountUnit';
import { toastUndo } from '../lib/toastUndo';

function CommentRow({ c, postId, currentUser, onLike, onEdit, onDelete, onReply, isReply = false }) {
  const [editing, setEditing] = React.useState(false);
  const [editText, setEditText] = React.useState(c.text);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLiked = (c.likes || []).includes(currentUser.id);
  const likeCount = (c.likes || []).length;
  const isOwn = c.user_id === currentUser.id;
  const isAdmin = currentUser.role === 'admin';

  const handleSaveEdit = async () => {
    if (!editText.trim() || editText === c.text) { setEditing(false); return; }
    await onEdit(editText.trim());
    setEditing(false);
  };

  const goToProfile = () => {
    if (c.user_id === currentUser.id) navigate('/my-stats');
    else navigate(`/profile/${c.user_id}`);
  };

  return (
    <div className={`flex gap-2 group ${isReply ? 'ml-8' : ''}`}>
      <Avatar className="h-7 w-7 shrink-0 mt-0.5 cursor-pointer" onClick={goToProfile}>
        {c.user_picture
          ? <AvatarImage src={c.user_picture} />
          : <AvatarFallback className="text-xs bg-muted">{(c.user_name || 'U')[0]}</AvatarFallback>
        }
      </Avatar>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex gap-2 items-center">
            <input
              className="flex-1 text-base md:text-sm bg-muted/50 rounded px-2 py-2 md:py-1 min-h-[44px] md:min-h-0 outline-none border border-border focus:border-primary"
              value={editText}
              onChange={e => setEditText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditing(false); }}
              autoFocus
            />
            <button onClick={handleSaveEdit} className="text-xs text-primary font-semibold">{t('common.save')}</button>
            <button onClick={() => setEditing(false)} className="text-xs text-muted-foreground">{t('common.cancel')}</button>
          </div>
        ) : (
          <div className="text-sm leading-snug">
            <div className="flex items-center gap-1">
              <span className="font-semibold cursor-pointer hover:underline" onClick={goToProfile}>{getDisplayName(c.user_name)}</span>
              <SubscriptionBadge badge={c.user_badge} />
            </div>
            <p className="mt-0.5">
              {c.text}
              {c.edited && <span className="text-xs text-muted-foreground ml-1">({t('social.edited')})</span>}
            </p>
          </div>
        )}
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-xs text-muted-foreground">
            {c.created_at ? formatDistanceToNow(parseISO(c.created_at), { addSuffix: true }) : ''}
          </span>
          {isOwn && !editing && (
            <>
              <button onClick={() => setEditing(true)} className="text-xs text-muted-foreground hover:text-foreground">{t('common.edit')}</button>
              <button onClick={onDelete} className="text-xs text-muted-foreground hover:text-destructive">{t('common.delete')}</button>
            </>
          )}
          {!isOwn && isAdmin && (
            <button onClick={onDelete} className="text-xs text-muted-foreground hover:text-destructive">{t('common.delete')}</button>
          )}
          {!isReply && onReply && (
            <button onClick={onReply} className="text-xs text-muted-foreground hover:text-foreground">{t('social.reply')}</button>
          )}
        </div>
      </div>
      <button
        onClick={onLike}
        className={`flex-shrink-0 flex flex-col items-center gap-0.5 pt-0.5 ${isLiked ? 'text-primary' : 'text-muted-foreground hover:text-primary'} transition-colors`}
      >
        <Heart className={`h-3.5 w-3.5 ${isLiked ? 'fill-current' : ''}`} />
        {likeCount > 0 && <span className="text-xs font-medium">{likeCount}</span>}
      </button>
    </div>
  );
}

const PR_COLORS = {
  weight:   'text-yellow-500',   // Yellow — all-time heaviest raw weight
  e1rm:     'text-purple-500',   // Purple — best estimated 1RM (more reps at sub-max weight)
  // Legacy backward compat
  progress: 'text-yellow-500',
  alltime:  'text-purple-500',
  reps:     'text-green-500',
};

const PR_LABEL_KEYS = {
  weight:   'social.prLabel.weight',
  e1rm:     'social.prLabel.e1rm',
  progress: 'social.prLabel.progress',
  alltime:  'social.prLabel.alltime',
  reps:     'social.prLabel.reps',
};

function PrTrophies({ prTypes, is_pr, className = 'h-3 w-3' }) {
  const { t } = useTranslation();
  const types = prTypes?.length > 0 ? prTypes : (is_pr ? ['weight'] : []);
  if (types.length === 0) return null;
  return types.map((type) => (
    <span key={type} title={PR_LABEL_KEYS[type] ? t(PR_LABEL_KEYS[type]) : type} className="inline-flex">
      <Trophy
        className={`${className} ${PR_COLORS[type] || 'text-yellow-500'}`}
        style={{ animation: 'bounce 0.7s ease-in-out 3' }}
      />
    </span>
  ));
}

const MUSCLE_COLORS = {
  Chest: '#ef4444', Back: '#3b82f6', Shoulders: '#8b5cf6', Biceps: '#f59e0b',
  Triceps: '#ec4899', Legs: '#22c55e', Quadriceps: '#22c55e', Hamstrings: '#16a34a',
  Glutes: '#14b8a6', Calves: '#06b6d4', Core: '#f97316', Abs: '#f97316',
  // target muscle names (lowercase)
  pectorals: '#ef4444', 'upper back': '#3b82f6', lats: '#3b82f6', rhomboids: '#3b82f6', back: '#3b82f6',
  'lower back': '#60a5fa', delts: '#8b5cf6', 'rear deltoids': '#8b5cf6', shoulders: '#8b5cf6',
  biceps: '#f59e0b', triceps: '#ec4899', quads: '#22c55e', hamstrings: '#16a34a',
  glutes: '#14b8a6', calves: '#06b6d4', abs: '#f97316', obliques: '#fb923c',
  'serratus anterior': '#fb923c', core: '#f97316', forearms: '#6366f1',
  traps: '#6366f1', adductors: '#ec4899', abductors: '#84cc16',
};

// Map bodyPart-style muscle_group to proper target muscle names
const MUSCLE_GROUP_TO_TARGET = {
  'upper arms': 'biceps', 'lower arms': 'forearms', 'upper legs': 'quads',
  'lower legs': 'calves', 'chest': 'pectorals', 'back': 'lats',
  'shoulders': 'delts', 'waist': 'abs', 'cardio': 'cardio',
};
// Normalize raw DB strings to the canonical target-muscle key we use app-wide.
// Two kinds of rewrites only: (a) synonyms of the same muscle, (b) bodyPart
// fallbacks for exercises whose target is missing and only a bodyPart is set.
// Deliberately NO target-to-target collapses (lats ≠ upper back, rhomboids ≠
// upper back, rear deltoids ≠ delts). The body-map paint layer sums keys
// visually where one SVG region covers several target muscles.
const SECONDARY_MUSCLE_NORM = {
  'deltoids': 'delts', 'trapezius': 'traps',
  'chest': 'pectorals', 'upper arms': 'biceps', 'lower arms': 'forearms',
  'upper legs': 'quads', 'lower legs': 'calves', 'waist': 'abs',
  'back': 'upper back', 'shoulders': 'delts',
  'spine': 'lower back',
};
const normalizeSecondary = (sm) => {
  const raw = (sm || '').toLowerCase();
  return SECONDARY_MUSCLE_NORM[raw] || raw;
};
// Selection sync helpers (identity now — kept as hooks in case future bucketing)
const splitNameToStatKey = (name) => name;
const statKeyToSplitName = (key) => key;
const mapMuscleTarget = (ex) => {
  if (ex.targetMuscles?.length > 0) return ex.targetMuscles[0];
  const mg = (ex.muscle_group || '').toLowerCase();
  return MUSCLE_GROUP_TO_TARGET[mg] || mg;
};

function ExerciseThumb({ name, gifUrl, size = 32 }) {
  const s = { width: size, height: size };
  return (
    <div className="rounded-full overflow-hidden bg-muted shrink-0" style={s}>
      <StaticExerciseImage src={gifUrl} alt={name} size="sm" />
    </div>
  );
}

const POST_TYPE_FILTERS = [
  { value: 'all', labelKey: 'social.filterAll', icon: null },
  { value: 'workout', labelKey: 'social.filterWorkouts', icon: Dumbbell },
  { value: 'meal', labelKey: 'social.filterMeals', icon: Apple },
  { value: 'checkin', labelKey: 'social.filterCheckins', icon: Scale },
];

/* Photo to base64 helper — uses shared compressor */
function photoToBase64(file) {
  return compressImage(file);
}

/* ── Full Edit Post Modal ── */
function EditPostModal({ post, open, onClose, onSave, onRefresh }) {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef(null);
  const [cropperOpen, setCropperOpen] = React.useState(false);
  const [cropperSrc, setCropperSrc] = React.useState(null);
  const [cropperField, setCropperField] = React.useState('');
  // Workout session edit
  const [showWorkoutEdit, setShowWorkoutEdit] = React.useState(false);
  const [workoutSession, setWorkoutSession] = React.useState(null);
  const [loadingSession, setLoadingSession] = React.useState(false);

  React.useEffect(() => {
    if (post) {
      const pt = post.post_type;
      if (pt === 'workout' || pt === 'workout_session') {
        setForm({
          description: post.description || '',
          workout_photo: post.workout_photo || '',
          day_name: post.day_name || '',
          duration_minutes: post.duration_minutes || 0,
          created_at: post.created_at ? post.created_at.slice(0, 16) : '',
          exercises_detail: (post.exercises_detail || []).map(ex => ({
            ...ex,
            name: ex.name || '',
            muscle_group: ex.muscle_group || '',
            sets: (ex.sets || []).map(s => ({ weight: s.weight || 0, reps: s.reps || 0, is_pr: s.is_pr || false, pr_types: s.pr_types || [] })),
          })),
        });
      } else if (pt === 'meal') {
        setForm({
          meal_type: post.meal_type || 'breakfast',
          meal_label: post.meal_label || '',
          meal_calories: post.meal_calories || 0,
          meal_protein: post.meal_protein || 0,
          meal_carbs: post.meal_carbs || 0,
          meal_fat: post.meal_fat || 0,
          meal_photo: post.meal_photo || '',
          meal_foods: (post.meal_foods || []).map(f => ({
            name: f.name || '', calories: f.calories || 0, protein: f.protein || 0,
            carbs: f.carbs || 0, fat: f.fat || 0,
          })),
        });
      } else if (pt === 'diet_day') {
        setForm({
          day_date: post.day_date || '',
          day_calories: post.day_calories || 0,
          day_protein: post.day_protein || 0,
          day_carbs: post.day_carbs || 0,
          day_fat: post.day_fat || 0,
          day_target_calories: post.day_target_calories || 2000,
          day_meals: (post.day_meals || []).map(m => ({
            meal_type: m.meal_type || '',
            meal_label: m.meal_label || '',
            meal_icon: m.meal_icon || '🍽️',
            calories: m.calories || 0,
            protein: m.protein || 0,
            carbs: m.carbs || 0,
            fat: m.fat || 0,
            foods: (m.foods || []).map(f => ({
              name: f.name || '', calories: f.calories || 0, protein: f.protein || 0,
              carbs: f.carbs || 0, fat: f.fat || 0,
            })),
          })),
          _activeMealTab: 0,
        });
      } else if (pt === 'checkin') {
        setForm({
          checkin_weight: post.checkin_weight || 0,
          checkin_body_fat: post.checkin_body_fat || '',
          checkin_notes: post.checkin_notes || '',
          checkin_mood: post.checkin_mood || '',
          checkin_photo_front: post.checkin_photo_front || '',
          checkin_photo_side: post.checkin_photo_side || '',
          checkin_photo_back: post.checkin_photo_back || '',
        });
      }
    }
  }, [post]);

  if (!post) return null;
  const pt = post.post_type;

  const handleFileSelect = (field) => {
    setCropperField(field);
    fileRef.current?.setAttribute('data-field', field);
    fileRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const field = fileRef.current?.getAttribute('data-field') || 'workout_photo';
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropperSrc(ev.target.result);
      setCropperField(field);
      setCropperOpen(true);
    };
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  const handleCropComplete = async (croppedDataUrl) => {
    setForm(f => ({ ...f, [cropperField]: croppedDataUrl }));
    setCropperOpen(false);
    setCropperSrc(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip internal-only fields before sending
      const { _activeMealTab, ...payload } = form;
      await onSave(post.id, payload);
      onClose();
    }
    catch { /* handled in parent */ }
    finally { setSaving(false); }
  };

  // --- Exercise editing helpers ---
  const updateExercise = (idx, key, val) => {
    setForm(f => {
      const exs = [...(f.exercises_detail || [])];
      exs[idx] = { ...exs[idx], [key]: val };
      return { ...f, exercises_detail: exs };
    });
  };
  const updateSet = (exIdx, setIdx, key, val) => {
    setForm(f => {
      const exs = [...(f.exercises_detail || [])];
      const sets = [...(exs[exIdx].sets || [])];
      sets[setIdx] = { ...sets[setIdx], [key]: val };
      exs[exIdx] = { ...exs[exIdx], sets };
      return { ...f, exercises_detail: exs };
    });
  };
  const addSet = (exIdx) => {
    setForm(f => {
      const exs = [...(f.exercises_detail || [])];
      exs[exIdx] = { ...exs[exIdx], sets: [...(exs[exIdx].sets || []), { weight: 0, reps: 0, is_pr: false }] };
      return { ...f, exercises_detail: exs };
    });
  };
  const removeSet = (exIdx, setIdx) => {
    setForm(f => {
      const exs = [...(f.exercises_detail || [])];
      exs[exIdx] = { ...exs[exIdx], sets: exs[exIdx].sets.filter((_, i) => i !== setIdx) };
      return { ...f, exercises_detail: exs };
    });
  };
  const addExercise = () => {
    setForm(f => ({
      ...f,
      exercises_detail: [...(f.exercises_detail || []), { name: '', muscle_group: '', sets: [{ weight: 0, reps: 0, is_pr: false }] }],
    }));
  };
  const removeExercise = (idx) => {
    setForm(f => ({ ...f, exercises_detail: (f.exercises_detail || []).filter((_, i) => i !== idx) }));
  };

  // --- Food editing helpers ---
  const updateFood = (idx, key, val) => {
    setForm(f => {
      const foods = [...(f.meal_foods || [])];
      foods[idx] = { ...foods[idx], [key]: val };
      return { ...f, meal_foods: foods };
    });
  };
  const addFood = () => {
    setForm(f => ({
      ...f,
      meal_foods: [...(f.meal_foods || []), { name: '', calories: 0, protein: 0, carbs: 0, fat: 0 }],
    }));
  };
  const removeFood = (idx) => {
    setForm(f => ({ ...f, meal_foods: (f.meal_foods || []).filter((_, i) => i !== idx) }));
  };

  // --- Diet Day editing helpers ---
  const updateDayMealFood = (mealIdx, foodIdx, key, val) => {
    setForm(f => {
      const meals = [...(f.day_meals || [])];
      const foods = [...(meals[mealIdx].foods || [])];
      foods[foodIdx] = { ...foods[foodIdx], [key]: val };
      meals[mealIdx] = { ...meals[mealIdx], foods };
      return { ...f, day_meals: meals };
    });
  };
  const addDayMealFood = (mealIdx) => {
    setForm(f => {
      const meals = [...(f.day_meals || [])];
      meals[mealIdx] = { ...meals[mealIdx], foods: [...(meals[mealIdx].foods || []), { name: '', calories: 0, protein: 0, carbs: 0, fat: 0 }] };
      return { ...f, day_meals: meals };
    });
  };
  const removeDayMealFood = (mealIdx, foodIdx) => {
    setForm(f => {
      const meals = [...(f.day_meals || [])];
      meals[mealIdx] = { ...meals[mealIdx], foods: meals[mealIdx].foods.filter((_, i) => i !== foodIdx) };
      return { ...f, day_meals: meals };
    });
  };
  const recalcDayMealTotals = (mealIdx) => {
    setForm(f => {
      const meals = [...(f.day_meals || [])];
      const foods = meals[mealIdx].foods || [];
      meals[mealIdx] = {
        ...meals[mealIdx],
        calories: foods.reduce((s, fd) => s + (Number(fd.calories) || 0), 0),
        protein: foods.reduce((s, fd) => s + (Number(fd.protein) || 0), 0),
        carbs: foods.reduce((s, fd) => s + (Number(fd.carbs) || 0), 0),
        fat: foods.reduce((s, fd) => s + (Number(fd.fat) || 0), 0),
      };
      // Also recalc day totals
      const updatedMeals = meals;
      return {
        ...f,
        day_meals: updatedMeals,
        day_calories: updatedMeals.reduce((s, m) => s + (Number(m.calories) || 0), 0),
        day_protein: updatedMeals.reduce((s, m) => s + (Number(m.protein) || 0), 0),
        day_carbs: updatedMeals.reduce((s, m) => s + (Number(m.carbs) || 0), 0),
        day_fat: updatedMeals.reduce((s, m) => s + (Number(m.fat) || 0), 0),
      };
    });
  };
  const recalcAllDayTotals = () => {
    setForm(f => {
      const meals = (f.day_meals || []).map(m => {
        const foods = m.foods || [];
        return {
          ...m,
          calories: foods.reduce((s, fd) => s + (Number(fd.calories) || 0), 0),
          protein: foods.reduce((s, fd) => s + (Number(fd.protein) || 0), 0),
          carbs: foods.reduce((s, fd) => s + (Number(fd.carbs) || 0), 0),
          fat: foods.reduce((s, fd) => s + (Number(fd.fat) || 0), 0),
        };
      });
      return {
        ...f,
        day_meals: meals,
        day_calories: meals.reduce((s, m) => s + (Number(m.calories) || 0), 0),
        day_protein: meals.reduce((s, m) => s + (Number(m.protein) || 0), 0),
        day_carbs: meals.reduce((s, m) => s + (Number(m.carbs) || 0), 0),
        day_fat: meals.reduce((s, m) => s + (Number(m.fat) || 0), 0),
      };
    });
  };

  // Auto-recalculate meal totals from foods
  const recalcMealTotals = () => {
    setForm(f => {
      const foods = f.meal_foods || [];
      if (foods.length === 0) return f;
      return {
        ...f,
        meal_calories: foods.reduce((s, fd) => s + (Number(fd.calories) || 0), 0),
        meal_protein: foods.reduce((s, fd) => s + (Number(fd.protein) || 0), 0),
        meal_carbs: foods.reduce((s, fd) => s + (Number(fd.carbs) || 0), 0),
        meal_fat: foods.reduce((s, fd) => s + (Number(fd.fat) || 0), 0),
      };
    });
  };

  const PhotoField = ({ label, field, aspectRatio = '9/16' }) => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1 relative max-w-[160px]">
        {form[field] ? (
          <div className="relative group">
            <img src={form[field]} alt={label} style={{ aspectRatio }} className="w-full object-cover rounded-lg" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => handleFileSelect(field)}>
                <Camera className="h-4 w-4 mr-1" />{t('social.changePhoto')}
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setForm(f => ({ ...f, [field]: '' }))}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <button onClick={() => handleFileSelect(field)}
            style={{ aspectRatio }}
            className="w-full rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors">
            <Camera className="h-6 w-6 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{t('social.addPhoto')}</span>
          </button>
        )}
      </div>
    </div>
  );

  const MEAL_TYPES = [
    { value: 'breakfast', label: t('diet.breakfast') },
    { value: 'lunch', label: t('diet.lunch') },
    { value: 'dinner', label: t('diet.dinner') },
    { value: 'snacks', label: t('diet.snacks') },
  ];

  const saveBtn = (
    <button onClick={handleSave} disabled={saving} className="text-sm font-bold text-primary disabled:opacity-50 px-2">
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
    </button>
  );

  return (
    <>
    <FullScreenPage
      open={open}
      onClose={onClose}
      title={`${t('social.editTitle')} ${pt === 'workout' || pt === 'workout_session' ? t('social.editTitleWorkout') : pt === 'meal' ? t('social.editTitleMeal') : pt === 'diet_day' ? t('social.editTitleNutritionDay') : t('social.editTitleCheckin')}`}
      rightAction={saveBtn}
      data-testid={`edit-post-modal-${post.id}`}
    >
        <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handleFileChange} />

        <div className="space-y-4">
          {/* ───── WORKOUT EDIT ───── */}
          {(pt === 'workout' || pt === 'workout_session') && (
            <>
              <div className="flex gap-4">
                <PhotoField label={t('social.workoutPhoto')} field="workout_photo" aspectRatio="9/16" />
                <div className="flex-1 space-y-3 min-w-0">
                  <div>
                    <Label className="text-base md:text-xs text-muted-foreground">{t('social.workoutName')}</Label>
                    <Input value={form.day_name || ''} onChange={e => setForm(f => ({ ...f, day_name: e.target.value }))} data-testid="edit-day-name" />
                  </div>
                  <div>
                    <Label className="text-base md:text-xs text-muted-foreground">{t('social.durationMin')}</Label>
                    <Input type="number" value={form.duration_minutes || ''} onChange={e => setForm(f => ({ ...f, duration_minutes: Number(e.target.value) }))} data-testid="edit-duration" />
                  </div>
                  <div>
                    <Label className="text-base md:text-xs text-muted-foreground">{t('social.workoutDateTime')}</Label>
                    <Input type="datetime-local" value={form.created_at || ''}
                      onChange={e => setForm(f => ({ ...f, created_at: e.target.value }))}
                      data-testid="edit-workout-date" />
                  </div>
                  <div>
                    <Label className="text-base md:text-xs text-muted-foreground">{t('common.description')}</Label>
                    <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm resize-none" rows={2} placeholder={t('social.howWasWorkout')} data-testid="edit-description" />
                  </div>
                </div>
              </div>

              {/* Exercises - read-only display with edit button that opens WorkoutEditDialog */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('social.exercises')}</Label>
                  {post.workout_session_id && (
                    <Button size="sm" variant="outline" className="text-xs h-11 md:h-7 text-primary border-primary/30 hover:bg-primary/90"
                      disabled={loadingSession}
                      onClick={async () => {
                        setLoadingSession(true);
                        try {
                          const res = await client.get('/workout-sessions/history?limit=200');
                          const sess = res.data.find(s => s.id === post.workout_session_id);
                          if (sess) {
                            setWorkoutSession(sess);
                            setShowWorkoutEdit(true);
                          } else {
                            toast.error(t('social.errorSessionNotFound'));
                          }
                        } catch { toast.error(t('social.errorLoadSession')); }
                        finally { setLoadingSession(false); }
                      }}
                      data-testid="edit-exercises-btn">
                      {loadingSession ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Pencil className="h-3 w-3 mr-1" /> {t('social.editExercisesSets')}</>}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {(form.exercises_detail || []).map((ex, exIdx) => (
                    <div key={exIdx} className="border rounded-lg p-3 bg-muted/50 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <ExerciseThumb name={ex.name} gifUrl={ex.gifUrl || ex.image_url} size={28} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{exerciseName(ex, language)}</p>
                          <div className="flex flex-wrap gap-1">
                            {(ex.targetMuscles || []).length > 0
                              ? ex.targetMuscles.map(m => (
                                  <span key={m} className="text-xs px-1.5 py-0.5 rounded border" style={{ borderColor: `${TARGET_MUSCLE_COLORS[m.toLowerCase()] || 'hsl(var(--border))'}40`, color: TARGET_MUSCLE_COLORS[m.toLowerCase()] || 'inherit' }}>
                                    {displayMuscleName(m, language)}
                                  </span>
                                ))
                              : ex.muscle_group ? <span className="text-xs text-muted-foreground capitalize">{translateMuscle(ex.muscle_group, language)}</span> : null
                            }
                          </div>
                        </div>
                      </div>
                      <div className="space-y-0.5">
                        {(ex.sets || []).map((s, sIdx) => {
                          const setType = SET_TYPES[s.set_type] || SET_TYPES.work;
                          const mode = getExerciseInputMode(ex);
                          return (
                            <div key={sIdx} className="flex items-center gap-2 text-xs">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${setType.color}`}>{sIdx + 1}</span>
                              {mode === 'cardio' ? (
                                <span className="font-mono">{formatCardioSetSummary(s, ex)}</span>
                              ) : mode === 'time' ? (
                                <span className="font-mono">{formatDuration(s.duration_seconds)}</span>
                              ) : (
                                <>
                                  <span className="font-mono">{s.weight || 0}kg</span>
                                  <span className="text-muted-foreground">x</span>
                                  <span className="font-mono">{s.reps || s.completed_reps || 0} {t('workout.reps')}</span>
                                </>
                              )}
                              <PrTrophies prTypes={s.pr_types} is_pr={s.is_pr} className="h-3 w-3" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {!post.workout_session_id && (
                    <p className="text-xs text-muted-foreground italic">{t('social.exercisesReadOnly')}</p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ───── MEAL EDIT ───── */}
          {pt === 'meal' && (
            <>
              <PhotoField label={t('social.mealPhoto')} field="meal_photo" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">{t('social.mealType')}</Label>
                  <select value={form.meal_type || 'breakfast'}
                    onChange={e => { const v = e.target.value; const labels = { breakfast: t('diet.breakfast'), lunch: t('diet.lunch'), dinner: t('diet.dinner'), snacks: t('diet.snacks') }; setForm(f => ({ ...f, meal_type: v, meal_label: labels[v] || v })); }}
                    className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm" data-testid="edit-meal-type">
                    {MEAL_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-base md:text-xs text-muted-foreground">{t('social.mealLabel')}</Label>
                  <Input value={form.meal_label || ''} onChange={e => setForm(f => ({ ...f, meal_label: e.target.value }))} data-testid="edit-meal-label" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.calories')}</Label>
                  <Input type="number" value={form.meal_calories || ''} onChange={e => setForm(f => ({ ...f, meal_calories: Number(e.target.value) }))} data-testid="edit-meal-calories" /></div>
                <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.protein')}</Label>
                  <Input type="number" value={form.meal_protein || ''} onChange={e => setForm(f => ({ ...f, meal_protein: Number(e.target.value) }))} data-testid="edit-meal-protein" /></div>
                <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.carbs')}</Label>
                  <Input type="number" value={form.meal_carbs || ''} onChange={e => setForm(f => ({ ...f, meal_carbs: Number(e.target.value) }))} data-testid="edit-meal-carbs" /></div>
                <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.fat')}</Label>
                  <Input type="number" value={form.meal_fat || ''} onChange={e => setForm(f => ({ ...f, meal_fat: Number(e.target.value) }))} data-testid="edit-meal-fat" /></div>
              </div>

              {/* Food Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('social.foodItems')}</Label>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-xs h-11 md:h-7 text-primary" onClick={recalcMealTotals} data-testid="recalc-totals-btn">{t('social.recalculate')}</Button>
                    <Button size="sm" variant="outline" className="text-xs h-9 md:h-7" onClick={addFood} data-testid="add-food-btn">+ {t('social.food')}</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {(form.meal_foods || []).map((food, idx) => (
                    <div key={idx} className="border rounded-lg p-2 space-y-1.5 bg-muted/50">
                      <div className="flex items-center gap-2">
                        <Input value={food.name} onChange={e => updateFood(idx, 'name', e.target.value)} placeholder={t('social.foodName')} className="text-base md:text-sm flex-1" data-testid={`edit-food-name-${idx}`} />
                        <Button size="icon" variant="ghost" className="h-11 w-11 md:h-8 md:w-8 text-destructive" onClick={() => removeFood(idx)} tooltip={t('tooltips.remove')}><X className="h-3.5 w-3.5" /></Button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                        <Input type="number" value={food.calories || ''} onChange={e => updateFood(idx, 'calories', Number(e.target.value))} placeholder="kcal" className="text-sm md:text-xs h-7" data-testid={`edit-food-cal-${idx}`} />
                        <Input type="number" value={food.protein || ''} onChange={e => updateFood(idx, 'protein', Number(e.target.value))} placeholder="P" className="text-sm md:text-xs h-7" data-testid={`edit-food-prot-${idx}`} />
                        <Input type="number" value={food.carbs || ''} onChange={e => updateFood(idx, 'carbs', Number(e.target.value))} placeholder="C" className="text-sm md:text-xs h-7" data-testid={`edit-food-carbs-${idx}`} />
                        <Input type="number" value={food.fat || ''} onChange={e => updateFood(idx, 'fat', Number(e.target.value))} placeholder="F" className="text-sm md:text-xs h-7" data-testid={`edit-food-fat-${idx}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ───── DIET DAY EDIT ───── */}
          {pt === 'diet_day' && (
            <>
              {/* Day summary */}
              <div className="rounded-xl bg-gradient-to-br from-[#0d1f14] to-[#1a2e1e] p-4 text-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🥗</span>
                    <div>
                      <h3 className="font-bold text-base" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('social.nutritionDay')}</h3>
                      <p className="text-xs text-white/50">{form.day_date}</p>
                    </div>
                  </div>
                  <p className="text-2xl font-black" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {Math.round(form.day_calories)} <span className="text-sm text-white/50">kcal</span>
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 rounded-lg bg-white/5">
                    <p className="text-base font-bold text-red-400">{Math.round(form.day_protein)}g</p>
                    <p className="text-xs text-white/50 uppercase">{t('social.protein')}</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-white/5">
                    <p className="text-base font-bold text-amber-400">{Math.round(form.day_carbs)}g</p>
                    <p className="text-xs text-white/50 uppercase">{t('social.carbs')}</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-white/5">
                    <p className="text-base font-bold text-blue-400">{Math.round(form.day_fat)}g</p>
                    <p className="text-xs text-white/50 uppercase">{t('social.fat')}</p>
                  </div>
                </div>
              </div>

              {/* Meal tabs */}
              {(form.day_meals || []).length > 0 && (
                <>
                  <div className="flex gap-1 overflow-x-auto pb-1">
                    {form.day_meals.map((m, i) => (
                      <button key={i} onClick={() => setForm(f => ({ ...f, _activeMealTab: i }))}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                          (form._activeMealTab || 0) === i
                            ? 'bg-primary text-white'
                            : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                        }`}>
                        <span>{m.meal_icon}</span>
                        <span>{m.meal_label}</span>
                        <span className="text-[10px] opacity-70">{Math.round(m.calories)} kcal</span>
                      </button>
                    ))}
                  </div>

                  {/* Active meal content */}
                  {(() => {
                    const mealIdx = form._activeMealTab || 0;
                    const meal = form.day_meals[mealIdx];
                    if (!meal) return null;
                    return (
                      <div className="space-y-3">
                        {/* Meal macro summary */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.calories')}</Label>
                            <Input type="number" value={meal.calories || ''} readOnly className="text-base md:text-sm bg-muted/30" /></div>
                          <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.protein')}</Label>
                            <Input type="number" value={meal.protein || ''} readOnly className="text-base md:text-sm bg-muted/30" /></div>
                          <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.carbs')}</Label>
                            <Input type="number" value={meal.carbs || ''} readOnly className="text-base md:text-sm bg-muted/30" /></div>
                          <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.fat')}</Label>
                            <Input type="number" value={meal.fat || ''} readOnly className="text-base md:text-sm bg-muted/30" /></div>
                        </div>

                        {/* Food items */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('social.foodItems')}</Label>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="text-xs h-11 md:h-7 text-primary" onClick={() => recalcDayMealTotals(mealIdx)}>{t('social.recalculate')}</Button>
                              <Button size="sm" variant="outline" className="text-xs h-9 md:h-7" onClick={() => addDayMealFood(mealIdx)}>+ {t('social.food')}</Button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {(meal.foods || []).map((food, idx) => (
                              <div key={idx} className="border rounded-lg p-2 space-y-1.5 bg-muted/50">
                                <div className="flex items-center gap-2">
                                  <Input value={food.name} onChange={e => updateDayMealFood(mealIdx, idx, 'name', e.target.value)} placeholder={t('social.foodName')} className="text-base md:text-sm flex-1" />
                                  <Button size="icon" variant="ghost" className="h-11 w-11 md:h-8 md:w-8 text-destructive" onClick={() => removeDayMealFood(mealIdx, idx)} tooltip={t('tooltips.remove')}><X className="h-3.5 w-3.5" /></Button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                                  <Input type="number" value={food.calories || ''} onChange={e => updateDayMealFood(mealIdx, idx, 'calories', Number(e.target.value))} placeholder="kcal" className="text-sm md:text-xs h-7" />
                                  <Input type="number" value={food.protein || ''} onChange={e => updateDayMealFood(mealIdx, idx, 'protein', Number(e.target.value))} placeholder="P" className="text-sm md:text-xs h-7" />
                                  <Input type="number" value={food.carbs || ''} onChange={e => updateDayMealFood(mealIdx, idx, 'carbs', Number(e.target.value))} placeholder="C" className="text-sm md:text-xs h-7" />
                                  <Input type="number" value={food.fat || ''} onChange={e => updateDayMealFood(mealIdx, idx, 'fat', Number(e.target.value))} placeholder="F" className="text-sm md:text-xs h-7" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </>
          )}

          {/* ───── CHECK-IN EDIT ───── */}
          {pt === 'checkin' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.weightKg')}</Label>
                  <Input type="number" step="0.1" value={form.checkin_weight || ''} onChange={e => setForm(f => ({ ...f, checkin_weight: Number(e.target.value) }))} data-testid="edit-checkin-weight" /></div>
                <div><Label className="text-base md:text-xs text-muted-foreground">{t('social.bodyFatPct')}</Label>
                  <Input type="number" step="0.1" value={form.checkin_body_fat || ''} onChange={e => setForm(f => ({ ...f, checkin_body_fat: Number(e.target.value) || null }))} data-testid="edit-checkin-bodyfat" /></div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <PhotoField label={t('social.photoFront')} field="checkin_photo_front" />
                <PhotoField label={t('social.photoSide')} field="checkin_photo_side" />
                <PhotoField label={t('social.photoBack')} field="checkin_photo_back" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t('social.mood')}</Label>
                <div className="flex gap-2 mt-1">
                  {[{v:'great',e:'😄'},{v:'good',e:'🙂'},{v:'neutral',e:'😐'},{v:'bad',e:'😟'},{v:'terrible',e:'😣'}].map(m => (
                    <button key={m.v} onClick={() => setForm(f => ({ ...f, checkin_mood: f.checkin_mood === m.v ? '' : m.v }))}
                      className={`text-2xl p-1 rounded-lg transition-all ${form.checkin_mood === m.v ? 'bg-primary/10 ring-2 ring-primary' : 'opacity-40 hover:opacity-70'}`}
                      data-testid={`edit-mood-${m.v}`}>
                      {m.e}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-base md:text-xs text-muted-foreground">{t('common.notes')}</Label>
                <textarea value={form.checkin_notes || ''} onChange={e => setForm(f => ({ ...f, checkin_notes: e.target.value }))}
                  className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm resize-none" rows={2} data-testid="edit-checkin-notes" />
              </div>
            </>
          )}
        </div>

        <div className="py-4" />

        {/* Image Cropper */}
        {cropperOpen && cropperSrc && (
          <ImageCropper
            open={cropperOpen}
            onClose={() => { setCropperOpen(false); setCropperSrc(null); }}
            imageSrc={cropperSrc}
            onCropComplete={handleCropComplete}
            aspectRatio={9/16}
            title={t('social.cropPhoto')}
            uploadContext="social"
          />
        )}
    </FullScreenPage>

    {/* WorkoutEditDialog for editing the underlying workout session */}
    {workoutSession && (
      <WorkoutEditDialog
        session={workoutSession}
        open={showWorkoutEdit}
        onClose={() => { setShowWorkoutEdit(false); setWorkoutSession(null); }}
        onSaved={() => { if (onRefresh) onRefresh(); onClose(); }}
      />
    )}
  </>
  );
}

export function FeedFilterBar({ filterType, setFilterType }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5 w-full" data-testid="feed-filter-bar">
      {POST_TYPE_FILTERS.map(f => {
        const Icon = f.icon;
        const active = filterType === f.value;
        return (
          <button key={f.value} onClick={() => setFilterType(f.value)}
            className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-full text-xs font-semibold transition-all ${
              active ? 'bg-primary text-white' : 'bg-muted/60 text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`filter-${f.value}`}>
            {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
            <span className="truncate">{t(f.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

export function filterPosts(posts, filterType) {
  if (filterType === 'all') return posts;
  return posts.filter(p => {
    if (filterType === 'workout') return p.post_type === 'workout' || p.post_type === 'workout_session' || p.post_type === 'pr';
    if (filterType === 'meal') return p.post_type === 'meal' || p.post_type === 'diet_day';
    if (filterType === 'checkin') return p.post_type === 'checkin';
    return true;
  });
}

export default function SocialPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const postDialog = useRoutableDialog('post');
  const highlightPostId = postDialog.id;
  const activeTab = searchParams.get('tab') === 'discover' ? 'discover' : 'feed';
  const setActiveTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'feed') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  // Load cached feed instantly, then refresh in background
  const cachedFeed = React.useMemo(() => {
    try {
      const raw = sessionStorage.getItem('social_feed_cache');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const [posts, setPosts] = useState(cachedFeed?.posts || []);
  const [loading, setLoading] = useState(!cachedFeed);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(cachedFeed?.has_more || false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [followStatusMap, setFollowStatusMap] = useState({});
  const loadMoreRef = useRef(null);

  const fetchFeed = useCallback(async (pageNum = 1, append = false) => {
    try {
      const apiScope = activeTab === 'feed' ? 'following' : 'discover';
      const res = await client.get(`/social/feed?page=${pageNum}&limit=15&scope=${apiScope}`);
      if (append) {
        setPosts(prev => [...prev, ...res.data.posts]);
      } else {
        setPosts(res.data.posts);
        // Cache first page (no photos in response — lightweight)
        if (pageNum === 1 && apiScope === 'discover') {
          try {
            sessionStorage.setItem('social_feed_cache', JSON.stringify({ posts: res.data.posts, has_more: res.data.has_more }));
          } catch {}
        }
      }
      setHasMore(res.data.has_more);
      setPage(pageNum);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('social.errorLoadFeed'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab, t]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    setPosts([]);
    setPage(1);
    setHasMore(false);
    setLoading(true);
    fetchFeed(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Deep-link: ?post=<id> for a post not in the currently loaded list
  // (shared link, notification, older post) always lands on Discover,
  // because Feed is limited to followed authors. Clicks on posts already
  // visible in the Feed do not flip tabs.
  useEffect(() => {
    if (!postDialog.id) return;
    if (activeTab === 'discover') return;
    if (loading) return;
    if (posts.some(p => p.id === postDialog.id)) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'discover');
      return next;
    }, { replace: true });
  }, [postDialog.id, activeTab, loading, posts, setSearchParams]);

  // Deep-link: fetch the single post when it isn't in the loaded list
  // (older post past the first paginated page, or just-posted).
  useEffect(() => {
    const postId = postDialog.id;
    if (!postId) return;
    if (loading) return;
    if (activeTab !== 'discover') return;
    if (posts.some(p => p.id === postId)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await client.get(`/social/posts/${postId}`);
        if (cancelled) return;
        setPosts(prev => prev.some(p => p.id === res.data.id) ? prev : [res.data, ...prev]);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || t('social.errorLoadPost'));
      }
    })();
    return () => { cancelled = true; };
  }, [postDialog.id, posts, loading, activeTab, t]);

  // Batch-fetch follow status for post authors we haven't seen yet
  const knownFollowIdsRef = useRef(new Set());
  useEffect(() => {
    if (!posts.length || !user) return;
    const newIds = [...new Set(
      posts
        .map(p => p.user_id)
        .filter(id => id && id !== user.id && !knownFollowIdsRef.current.has(id))
    )];
    if (!newIds.length) return;
    newIds.forEach(id => knownFollowIdsRef.current.add(id));
    client.post('/follows/batch-status', { user_ids: newIds })
      .then(res => setFollowStatusMap(prev => ({ ...prev, ...res.data })))
      .catch(() => {
        newIds.forEach(id => knownFollowIdsRef.current.delete(id));
      });
  }, [posts, user]);

  const scrollStateRef = useRef({ hasMore, loadingMore, loading, page, fetchFeed });
  useEffect(() => {
    scrollStateRef.current = { hasMore, loadingMore, loading, page, fetchFeed };
  }, [hasMore, loadingMore, loading, page, fetchFeed]);

  // Infinite scroll - IntersectionObserver (created once)
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const { hasMore, loadingMore, loading, page, fetchFeed } = scrollStateRef.current;
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          setLoadingMore(true);
          fetchFeed(page + 1, true);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = () => {
    setLoadingMore(true);
    fetchFeed(page + 1, true);
  };

  const handleLike = useCallback(async (postId) => {
    haptics.light();

    // Capture current state for potential revert
    setPosts(prev => {
      const post = prev.find(p => p.id === postId);
      if (!post) return prev;
      const currentLikes = post.likes || [];
      const wasLiked = currentLikes.some(l => l.user_id === user.id);
      const optimisticLikes = wasLiked
        ? currentLikes.filter(l => l.user_id !== user.id)
        : [...currentLikes, { user_id: user.id, user_name: user.name }];
      return prev.map(p => p.id === postId ? { ...p, likes: optimisticLikes } : p);
    });

    try {
      const res = await client.post(`/social/posts/${postId}/like`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: res.data.likes } : p));
    } catch {
      toast.error(t('social.errorLikePost'));
      // Re-fetch to restore correct state
      fetchFeed();
    }
  }, [user?.id, user?.name, fetchFeed]);

  const handleComment = useCallback(async (postId, text) => {
    if (!text.trim()) return;
    try {
      const res = await client.post(`/social/posts/${postId}/comment`, { text });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: [...(p.comments || []), res.data.comment], comment_count: (p.comment_count || 0) + 1 } : p));
    } catch { toast.error(t('social.errorPostComment')); }
  }, []);

  const handleDeleteComment = useCallback(async (postId, commentId) => {
    try {
      await client.delete(`/social/posts/${postId}/comment/${commentId}`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: (p.comments || []).filter(c => c.id !== commentId), comment_count: Math.max(0, (p.comment_count || 0) - 1) } : p));
    } catch { toast.error(t('social.errorDeleteComment')); }
  }, []);

  // Fetch comments on-demand when user opens a comment section
  const fetchComments = useCallback(async (postId) => {
    try {
      const res = await client.get(`/social/posts/${postId}/comments`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: res.data.comments } : p));
    } catch { toast.error(t('social.errorLoadComments')); }
  }, []);

  // Delayed commit: there is no un-delete endpoint for posts, so instead of
  // deleting and trying to recreate, we hide the post and only send the DELETE
  // once the undo window closes. Undo simply puts the post back — nothing was
  // ever destroyed server-side.
  const handleDeletePost = useCallback(async (postId) => {
    let removed = null;
    let removedIndex = -1;
    setPosts((prev) => {
      removedIndex = prev.findIndex((p) => p.id === postId);
      removed = removedIndex >= 0 ? prev[removedIndex] : null;
      return prev.filter((p) => p.id !== postId);
    });
    if (!removed) return;

    toastUndo(
      t('undo.postDeleted', 'Post deleted'),
      () => {
        // Restore at its original position so the feed order is unchanged.
        setPosts((prev) => {
          if (prev.some((p) => p.id === postId)) return prev;
          const next = [...prev];
          next.splice(Math.max(0, removedIndex), 0, removed);
          return next;
        });
      },
      {
        onExpire: async () => {
          try {
            await client.delete(`/social/posts/${postId}`);
          } catch (err) {
            // Commit failed — put it back rather than leaving the UI lying.
            setPosts((prev) => (prev.some((p) => p.id === postId) ? prev : [...prev, removed]));
            toast.error(err.response?.data?.detail || t('social.errorDeletePost'));
          }
        },
      }
    );
  }, [t]);

  const handleReportPost = useCallback(async (postId) => {
    try {
      await client.post(`/social/posts/${postId}/report`);
      toast.success(t('social.postReported'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('social.errorReportPost'));
    }
  }, []);

  const { isRefreshing, pullDistance, handlers: pullHandlers } = usePullToRefresh(
    () => fetchFeed(1, false),
    { disabled: loading }
  );

  const filteredPosts = React.useMemo(() => filterPosts(posts, filterType), [posts, filterType]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    // Widened from max-w-lg (512px) to max-w-xl (576px) on desktop so the
    // feed column doesn't look anaemic in the wide admin viewport.
    <div className="max-w-2xl md:max-w-xl mx-auto pb-20 md:pb-0 space-y-4" data-testid="social-page" {...pullHandlers}>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-3">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="feed">{t('social.feedTab')}</TabsTrigger>
          <TabsTrigger value="discover">{t('social.discoverTab')}</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex items-center justify-between">
        <div>
          {/* Heading reflects which tab is active. Was hard-coded to "SOCIAL FEED /
              Feed" even on the Discover tab — confusing because the segmented
              control above already says "Discover". */}
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Users className="inline h-7 w-7 mr-2 text-primary" />
            {activeTab === 'discover'
              ? <>DISCOVER <span className="text-primary">FEED</span></>
              : <>SOCIAL <span className="text-primary">FEED</span></>}
          </h1>
          <p className="text-muted-foreground mt-1">{activeTab === 'discover' ? t('social.discoverTab', 'Discover') : t('social.feed')}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <FeedFilterBar filterType={filterType} setFilterType={setFilterType} />

      {filteredPosts.length === 0 ? (
        <>
          {posts.length === 0 && !loading && activeTab === 'feed' && (
            <FollowSuggestionsCard
              variant="empty"
              onBrowseDiscover={() => setActiveTab('discover')}
            />
          )}
          {!(posts.length === 0 && !loading && activeTab === 'feed') && (
            <Card className="border-border/50">
              <CardContent className="py-16 text-center">
                <Filter className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-semibold mb-1">{filterType === 'all' ? t('social.noPosts') : t('social.noFilteredPosts', { type: filterType })}</h3>
                <p className="text-muted-foreground text-sm">{filterType === 'all' ? t('social.beFirstToPost') : t('social.tryDifferentFilter')}</p>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <>
          {filteredPosts.map(post => (
            <SocialPost
              key={post.id}
              post={post}
              currentUser={user}
              onLike={handleLike}
              onComment={handleComment}
              onDeleteComment={handleDeleteComment}
              navigate={navigate}
              onDeletePost={handleDeletePost}
              onReportPost={handleReportPost}
              onPostUpdated={(updated) => setPosts(prev => prev.map(p => p.id === updated.id ? updated : p))}
              setPosts={setPosts}
              highlightPostId={highlightPostId}
              followStatusMap={followStatusMap}
              setFollowStatusMap={setFollowStatusMap}
              fetchComments={fetchComments}
            />
          ))}
          {activeTab === 'feed' && !hasMore && posts.length > 0 && (
            <div className="mt-4">
              <FollowSuggestionsCard
                variant="caught-up"
                onBrowseDiscover={() => setActiveTab('discover')}
              />
            </div>
          )}
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-6" data-testid="infinite-scroll-sentinel">
              {loadingMore && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">{t('common.loading')}</span>
                </div>
              )}
            </div>
          )}
          {!hasMore && posts.length > 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              {t('social.allPostsSeen')}
            </div>
          )}
        </>
      )}

    </div>
  );
}

export const SocialPost = React.memo(function SocialPost(props) {
  const editDialog = useRoutableDialog('edit');
  const [editingPost, setEditingPost] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const { t } = useTranslation();

  // URL → state sync: when ?edit=<id> matches THIS post, open the modal.
  // Each SocialPost instance wraps exactly one post; only the matching instance
  // should render the EditPostModal. Otherwise every post in the feed would
  // open a stub modal (no post_type → empty body, "Edit Check-in" title).
  useEffect(() => {
    if (!editDialog.id || props.post?.id !== editDialog.id) {
      if (editingPost !== null) setEditingPost(null);
      return;
    }
    if (editingPost?.id !== editDialog.id) {
      setEditingPost(props.post);
    }
  }, [editDialog.id, props.post]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditSave = async (postId, data) => {
    const res = await client.put(`/social/posts/${postId}`, data);
    if (props.onPostUpdated) props.onPostUpdated(res.data);
    toast.success(t('social.postUpdated'));
  };

  const handleDeleteRequest = (postId) => setDeleteConfirmId(postId);
  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    if (props.onDeletePost) await props.onDeletePost(deleteConfirmId);
    setDeleteConfirmId(null);
  };

  const propsWithOverrides = { ...props, onEditPost: (p) => editDialog.open(p.id), onDeletePost: handleDeleteRequest };

  return (
    <>
      {props.post.post_type === 'meal' ? <MealPost {...propsWithOverrides} /> :
       props.post.post_type === 'checkin' ? <CheckInPost {...propsWithOverrides} /> :
       props.post.post_type === 'diet_day' ? <DietDayPost {...propsWithOverrides} /> :
       props.post.post_type === 'pr' ? <PrPost {...propsWithOverrides} /> :
       props.post.post_type === 'achievement' ? <AchievementPost {...propsWithOverrides} /> :
       <WorkoutPost {...propsWithOverrides} />}
      <EditPostModal post={editingPost} open={!!editingPost} onClose={() => editDialog.close()} onSave={handleEditSave} onRefresh={async () => {
        if (editingPost && props.onPostUpdated) {
          try {
            const res = await client.get(`/social/posts/${editingPost.id}`);
            props.onPostUpdated(res.data);
          } catch (err) { toast.error(err?.response?.data?.detail || t('social.failedRefreshPost')); }
        }
      }} />
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-post-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Trash2 className="h-5 w-5 text-destructive" />{t('social.deletePostTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('social.confirmDeletePost')}</p>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1">{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={confirmDelete} className="flex-1" data-testid="confirm-delete-post-btn">{t('common.delete')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}, (prev, next) => {
  // Only re-render if the post data or key props actually changed
  return prev.post === next.post &&
    prev.currentUser?.id === next.currentUser?.id &&
    prev.followStatusMap === next.followStatusMap;
});

function WorkoutPost({ post, currentUser, onLike, onComment, onDeleteComment, navigate, onDeletePost, onEditPost, onReportPost, highlightPostId, setPosts, followStatusMap, setFollowStatusMap, fetchComments }) {
  const { language } = useLanguage();
  const postDialog = useRoutableDialog('post');
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [carouselSlide, setCarouselSlide] = useState(0);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    setShowDetailModal(postDialog.id === post.id);
  }, [postDialog.id, post.id]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showStoryGenerator, setShowStoryGenerator] = useState(false);
  const [personalRoutines, setPersonalRoutines] = useState([]);
  const [saveChoice, setSaveChoice] = useState('new');
  const [saveTarget, setSaveTarget] = useState('');
  const [saveNewName, setSaveNewName] = useState('');
  const [saveDayName, setSaveDayName] = useState(post.day_name || 'Workout');
  const [saving, setSaving] = useState(false);
  const [previewExercise, setPreviewExercise] = useState(null);
  const [selectedMapMuscle, setSelectedMapMuscle] = useState(null);
  const postRef = React.useRef(null);
  const commentsLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (highlightPostId === post.id && postRef.current) {
      postRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightPostId, post.id]);

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoadedRef.current && fetchComments) {
      commentsLoadedRef.current = true;
      fetchComments(post.id);
    }
  };

  const isLiked = (post.likes || []).some(l => l.user_id === currentUser.id);
  const likesCount = (post.likes || []).length;
  const commentsCount = post.comment_count ?? (post.comments || []).length;
  const canDelete = post.user_id === currentUser.id || currentUser.role === 'admin';

  const { t } = useTranslation();
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('time.justNow');
    if (mins < 60) return `${mins}m ${t('time.ago')}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${t('time.ago')}`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ${t('time.ago')}`;
    return formatDate(dateStr);
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText);
    setCommentText('');
    setSubmitting(false);
    setShowComments(true);
  };

  const handleUserClick = (userId) => {
    if (userId === currentUser.id) navigate('/my-stats');
    else navigate(`/profile/${userId}`);
  };

  const handleStartWorkout = () => {
    if (!post.exercises_detail?.length) {
      toast.error(t('social.errorNoExerciseData'));
      return;
    }
    const day = {
      id: `social-${post.id}`,
      name: post.day_name || 'Workout',
      day_name: post.day_name || 'Workout',
      exercises: post.exercises_detail.map((ex, exIdx) => ({
        id: `${post.id}-ex-${exIdx}`,
        name: ex.name || '',
        muscle_group: ex.muscle_group || '',
        targetMuscles: ex.targetMuscles || [],
        secondaryMuscles: ex.secondaryMuscles || [],
        rest_seconds: 90,
        notes: '',
        sets: (ex.sets || []).map((s, i) => ({
          set_number: i + 1,
          set_type: s.set_type || 'work',
          target_reps_min: s.completed_reps || s.reps || 8,
          target_reps_max: s.completed_reps || s.reps || 12,
        })),
      })),
    };
    navigate('/workout', { state: { startFromSocial: day } });
  };

  const openSaveToRoutine = async () => {
    try {
      const res = await client.get('/routines/personal');
      setPersonalRoutines(res.data);
    } catch (err) { toast.error(err?.response?.data?.detail || t('social.errorLoadRoutines')); }
    setSaveChoice('new');
    setSaveTarget('');
    setSaveNewName('My Routine');
    setSaveDayName(post.day_name || 'Workout');
    setShowSaveDialog(true);
  };

  const handleSaveToRoutine = async () => {
    setSaving(true);
    try {
      const exercises = (post.exercises_detail || []).map(ex => ({
        name: ex.name,
        muscle_group: ex.muscle_group,
        sets: (ex.sets || []).map(s => ({
          set_type: s.type || s.set_type || 'work',
          target_reps_min: s.reps || s.completed_reps || 8,
          target_reps_max: s.reps || s.completed_reps || 12,
        }))
      }));
      const payload = { day_name: saveDayName, exercises };
      if (saveChoice === 'existing' && saveTarget) {
        payload.routine_id = saveTarget;
      } else {
        payload.new_routine_name = saveNewName || 'My Routine';
      }
      await client.post('/routines/personal/save-workout', payload);
      toast.success(t('social.workoutSaved'));
      setShowSaveDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('social.errorSaveWorkout'));
    } finally {
      setSaving(false);
    }
  };

  const userInitialsRaw = getDisplayInitials(post.user_name);
  const userInitials = userInitialsRaw === '?' ? 'U' : userInitialsRaw;
  const hasExercises = (post.exercises_detail || []).length > 0;
  const hasPhoto = !!post.workout_photo;
  // WITH photo: [photo+stats, exercises, muscle analysis] = up to 3 slides
  // NO photo:  [stats+muscle analysis merged, exercises] = up to 2 slides
  const totalSlides = hasPhoto
    ? (hasExercises ? 3 : 1)
    : (hasExercises ? 2 : 1);

  // Calculate muscle stats for MuscleBodyMap using targetMuscles
  const postMuscleStats = React.useMemo(() => {
    const stats = {};
    (post.exercises_detail || []).forEach(ex => {
      const setsCount = ex.sets?.length || 1;
      const targets = ex.targetMuscles?.length > 0 ? ex.targetMuscles : [mapMuscleTarget(ex)];
      const primaryKeys = new Set();
      targets.forEach(tm => {
        const key = normalizeSecondary(tm);
        if (key) primaryKeys.add(key);
      });
      primaryKeys.forEach(key => { stats[key] = (stats[key] || 0) + setsCount; });
      const secondary = ex.secondaryMuscles || ex.secondary_muscles || [];
      const secondaryKeys = new Set();
      secondary.forEach(sm => {
        const key = normalizeSecondary(sm);
        if (key && !primaryKeys.has(key)) secondaryKeys.add(key);
      });
      secondaryKeys.forEach(key => { stats[key] = (stats[key] || 0) + setsCount * 0.5; });
    });
    return stats;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.exercises_detail]);

  const workoutSwipe = useSwipe(
    () => { if (carouselSlide < totalSlides - 1) setCarouselSlide(s => s + 1); },
    () => { if (carouselSlide > 0) setCarouselSlide(s => s - 1); }
  );

  // Muscle split percentages using target + secondary muscles (secondary = 0.5 weight)
  const muscleSplit = React.useMemo(() => {
    const counts = {};
    (post.exercises_detail || []).forEach(ex => {
      const setsCount = ex.sets?.length || 1;
      const targets = ex.targetMuscles?.length > 0 ? ex.targetMuscles : [mapMuscleTarget(ex)];
      const primaryKeys = new Set();
      targets.forEach(tm => {
        const key = normalizeSecondary(tm);
        if (key) primaryKeys.add(key);
      });
      primaryKeys.forEach(key => { counts[key] = (counts[key] || 0) + setsCount; });
      const secondary = ex.secondaryMuscles || ex.secondary_muscles || [];
      const secondaryKeys = new Set();
      secondary.forEach(sm => {
        const key = normalizeSecondary(sm);
        if (key && !primaryKeys.has(key)) secondaryKeys.add(key);
      });
      secondaryKeys.forEach(key => { counts[key] = (counts[key] || 0) + setsCount * 0.5; });
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return Object.entries(counts).map(([name, count]) => ({
      name, count, pct: total > 0 ? Math.round((count / total) * 100) : 0
    })).sort((a, b) => b.count - a.count);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.exercises_detail]);

  // Muscle-group split for the thumbnail — classifies each exercise by its
  // PRIMARY target's group and credits all of its sets to that group only.
  // No secondary credit at the group level: rows don't leak biceps into Arms,
  // bench press doesn't leak triceps into Arms, etc. This shows workout
  // classification ("upper day, mostly chest+back") rather than synergist
  // accounting. The detail modal keeps per-muscle secondary credit in
  // `muscleSplit` for deeper volume analysis.
  const muscleGroupSplit = React.useMemo(() => {
    const counts = {};
    (post.exercises_detail || []).forEach(ex => {
      const setsCount = ex.sets?.length || 1;
      const targets = ex.targetMuscles?.length > 0 ? ex.targetMuscles : [mapMuscleTarget(ex)];
      const primaryTarget = normalizeSecondary(targets[0] || '');
      const group = TARGET_TO_GROUP[primaryTarget];
      if (!group) return;
      counts[group] = (counts[group] || 0) + setsCount;
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return Object.entries(counts).map(([name, count]) => ({
      name, count, pct: total > 0 ? Math.round((count / total) * 100) : 0,
    })).sort((a, b) => b.count - a.count);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.exercises_detail]);

  // Derive a human display name when the post has the default "Custom Workout"
  // label. Format: "{Groups} - {TimeOfDay} Workout" (e.g. "Legs - Afternoon Workout").
  const displayDayName = React.useMemo(() => {
    const raw = post.day_name;
    if (raw && raw !== 'Custom Workout') return raw;
    const groups = muscleGroupSplit.map(g => g.name);
    if (!groups.length) return raw || t('workout.title');
    const groupLabel = groups.length <= 2
      ? groups.join(' & ')
      : groups.slice(0, -1).join(', ') + ' & ' + groups[groups.length - 1];
    const hour = new Date(post.created_at).getHours();
    const timeKey = hour < 5 ? 'night'
      : hour < 12 ? 'morning'
      : hour < 17 ? 'afternoon'
      : hour < 21 ? 'evening'
      : 'night';
    return `${groupLabel} - ${t(`time.${timeKey}`)} ${t('workout.title')}`;
  }, [post.day_name, post.created_at, muscleGroupSplit, t]);

  const getSetPillClass = (s) => {
    if (s.set_type === 'warmup') return 'bg-amber-500/15 text-amber-500 border border-amber-500/20';
    if (s.set_type === 'backoff') return 'bg-sky-500/15 text-sky-400 border border-sky-500/20';
    if (s.set_type === 'failure') return 'bg-red-500/15 text-red-400 border border-red-500/20';
    if (s.set_type === 'drop') return 'bg-teal-500/15 text-teal-400 border border-teal-500/20';
    return 'bg-primary/10 text-primary border border-primary/30';
  };

  const getSetLabel = (s) => {
    if (s.set_type === 'warmup') return <span className="font-bold mr-0.5">[W]</span>;
    if (s.set_type === 'drop') return <span className="font-bold mr-0.5">[D]</span>;
    if (s.set_type === 'backoff') return <span className="font-bold mr-0.5">[B]</span>;
    if (s.set_type === 'failure') return <span className="font-bold mr-0.5">[F]</span>;
    return null;
  };

  const getPrRingClass = (s) => {
    if (!s.is_pr && !s.pr_types?.length) return '';
    // Purple ring for e1RM PR (more reps at sub-max weight)
    if (s.pr_types?.includes('e1rm') || s.pr_types?.includes('alltime')) return 'ring-1 ring-purple-500/70';
    if (s.pr_types?.includes('weight') || s.pr_types?.includes('progress')) return 'ring-1 ring-yellow-500/70';
    if (s.pr_types?.includes('reps')) return 'ring-1 ring-green-500/70';
    return 'ring-1 ring-yellow-500/70';
  };

  const getPrBoldClass = (s) => (s.is_pr || s.pr_types?.length > 0) ? 'font-bold' : '';

  return (
    <>
    <Card ref={postRef} className={`border-border/50 overflow-hidden ${highlightPostId === post.id ? 'ring-2 ring-primary/50' : ''}`} data-testid={`social-post-${post.id}`}>
      <PostHeader post={post} currentUser={currentUser} canDelete={canDelete}
        onDeletePost={onDeletePost} onEditPost={onEditPost} onReportPost={onReportPost}
        handleUserClick={handleUserClick} timeAgo={timeAgo} userInitials={userInitials} t={t}
        handleStartWorkout={handleStartWorkout} openSaveToRoutine={openSaveToRoutine} followStatusMap={followStatusMap} setFollowStatusMap={setFollowStatusMap} />

      {/* Description */}
      {post.description && (
        <div className="px-4 pb-2">
          <p className="text-sm text-foreground">{post.description}</p>
        </div>
      )}

      {/* Swipeable Carousel — always 4:5 ratio so all cards are the same size */}
      <div className="relative mx-4 rounded-xl overflow-hidden cursor-pointer bg-card dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-[#2a1a0a] aspect-[4/5]"
        onClick={() => { if (!workoutSwipe.didSwipe.current) postDialog.open(post.id); }}
        {...{onTouchStart: workoutSwipe.onTouchStart, onTouchMove: workoutSwipe.onTouchMove, onTouchEnd: workoutSwipe.onTouchEnd}}
        data-testid={`workout-carousel-${post.id}`}>
        <div className="flex transition-transform duration-300 ease-out h-full" style={{ transform: `translateX(-${carouselSlide * 100}%)` }}>

          {/* ── SLIDE 1 ── */}
          <div className="min-w-full h-full bg-card dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-[#2a1a0a] text-foreground">
            {hasPhoto ? (
              /* WITH PHOTO: Photo (4:5 vertical) with overlay stats at bottom */
              <div className="relative w-full h-full">
                <PinchZoomImage src={post.workout_photo} alt="Workout" className="w-full h-full object-cover object-top" />
                {/* Gradient overlay at bottom for readability */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-16 pb-4 px-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Dumbbell className="h-4 w-4 text-primary" />
                    <h3 className="font-bold text-base text-white drop-shadow-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{displayDayName}</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 mb-2">
                    <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-lg px-2 py-1.5">
                      <Clock className="h-3.5 w-3.5 text-primary shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-bold leading-none text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{post.duration_minutes}</p>
                        <p className="text-xs text-white/50 uppercase leading-tight">{t('time.minutes')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-lg px-2 py-1.5">
                      <Flame className="h-3.5 w-3.5 text-green-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-bold leading-none text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{post.exercises_count}</p>
                        <p className="text-xs text-white/50 uppercase leading-tight">{post.exercises_count === 1 ? t('workout.exercise', 'Exercise') : t('workout.exercises')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-lg px-2 py-1.5">
                      <Trophy className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-bold leading-none text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{post.prs_achieved || 0}</p>
                        <p className="text-xs text-white/50 uppercase leading-tight">PR{post.prs_achieved === 1 ? '' : 's'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {muscleGroupSplit.map(g => (
                      <span key={g.name} className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${MUSCLE_GROUP_COLORS[g.name] || '#6b7280'}33`, color: MUSCLE_GROUP_COLORS[g.name] || '#9ca3af' }}>
                        {g.name}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Details eye icon at top right */}
                <button className="absolute top-3 right-3 flex items-center gap-1 text-xs text-white/70 bg-black/30 backdrop-blur-sm rounded-full px-2 py-1" onClick={(e) => { e.stopPropagation(); postDialog.open(post.id); }}>
                  <Eye className="h-3 w-3" /> {t('common.details')}
                </button>
              </div>
            ) : (
              /* NO PHOTO: Muscle Analysis on top, stats at bottom (same 4:5 ratio) */
              <div className="relative p-4 flex flex-col h-full">
                {/* Details button */}
                <button className="absolute top-3 right-3 flex items-center gap-1 text-xs text-foreground/70 bg-muted/60 backdrop-blur-sm rounded-full px-2 py-1 z-10"
                  onClick={(e) => { e.stopPropagation(); postDialog.open(post.id); }}>
                  <Eye className="h-3 w-3" /> {t('common.details')}
                </button>
                {/* Name */}
                <div className="flex items-center gap-2 mb-2 pr-20">
                  <Dumbbell className="h-5 w-5 text-primary shrink-0" />
                  <h3 className="font-bold text-lg truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{displayDayName}</h3>
                </div>
                {/* Muscle Analysis — fills middle space */}
                {Object.keys(postMuscleStats).length > 0 && (
                  <div className="flex-1 min-h-0 flex flex-col">
                    <div className="flex items-center justify-center gap-1.5 mb-1 shrink-0">
                      <Flame className="h-3 w-3 text-primary" />
                      <span className="font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('social.muscleAnalysis')}</span>
                    </div>
                    <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden">
                      <MuscleBodyMap muscleStats={postMuscleStats} size="xs" interactive hideExtras colorMode="daily"
                        selectedMuscle={selectedMapMuscle}
                        onSelect={(m) => setSelectedMapMuscle(prev => prev === m ? null : m)}
                      />
                    </div>
                    {muscleGroupSplit.length > 0 && (
                      <div className="shrink-0 border-t border-border pt-1.5 mb-2">
                        <div className={`grid gap-2 ${['','grid-cols-1','grid-cols-2','grid-cols-3','grid-cols-4','grid-cols-5'][Math.min(muscleGroupSplit.length,5)]}`}>
                          {muscleGroupSplit.map(g => (
                            <div key={g.name} className="flex flex-col items-center gap-0.5 rounded p-0.5">
                              <span className="text-xs truncate w-full text-center text-muted-foreground">{g.name}</span>
                              <div className="w-full h-1.5 rounded-full bg-muted/50 overflow-hidden my-0.5">
                                <div className="h-full rounded-full transition-all" style={{ width: `${g.pct}%`, backgroundColor: MUSCLE_GROUP_COLORS[g.name] || '#6b7280' }} />
                              </div>
                              <span className="text-xs font-bold text-foreground/80">{Math.round(g.count)} {t('workout.sets')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Exercise icon strip */}
                {post.exercises_detail && post.exercises_detail.length > 0 && (
                  <div className="shrink-0 flex items-center gap-2 mb-2 flex-wrap">
                    {post.exercises_detail.slice(0, 5).map((ex, i) => (
                      <div key={i} className="flex flex-col items-center gap-0.5">
                        <ExerciseThumb name={ex.name} gifUrl={ex.gifUrl || ex.image_url} size={28} />
                      </div>
                    ))}
                    {post.exercises_detail.length > 5 && (
                      <span className="text-xs text-foreground/50 font-medium">+{post.exercises_detail.length - 5} {t('common.more')}</span>
                    )}
                  </div>
                )}
                {/* Stats row — pinned to bottom */}
                <div className="grid grid-cols-3 gap-1.5 shrink-0 border-t border-border pt-2">
                  <div className="flex items-center gap-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-lg px-2 py-1.5">
                    <Clock className="h-3.5 w-3.5 text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{post.duration_minutes}</p>
                      <p className="text-xs text-muted-foreground uppercase leading-tight">{t('time.minutes')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-lg px-2 py-1.5">
                    <Flame className="h-3.5 w-3.5 text-green-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{post.exercises_count}</p>
                      <p className="text-xs text-muted-foreground uppercase leading-tight">{post.exercises_count === 1 ? t('workout.exercise', 'Exercise') : t('workout.exercises')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-lg px-2 py-1.5">
                    <Trophy className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{post.prs_achieved || 0}</p>
                      <p className="text-xs text-muted-foreground uppercase leading-tight">PR{post.prs_achieved === 1 ? '' : 's'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── SLIDE 2: EXERCISES ── */}
          {hasExercises && (
            <div className="min-w-full bg-card dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-[#1a0a0a] p-5 text-foreground overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                <Dumbbell className="h-4 w-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('social.exercises')} ({post.exercises_detail.length})</h4>
              </div>
              <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: hasPhoto ? 'calc(100% - 80px)' : '300px' }}>
                {post.exercises_detail.map((ex, i) => {
                  const exTarget = mapMuscleTarget(ex);
                  return (
                    <div key={i} className="flex items-center justify-between text-sm bg-muted/50 rounded-lg px-3 py-2 cursor-pointer active:opacity-70" onClick={(e) => { e.stopPropagation(); setPreviewExercise(ex); }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-primary font-mono text-xs w-5">{i + 1}.</span>
                        <ExerciseThumb name={ex.name} gifUrl={ex.gifUrl || ex.image_url} size={28} />
                        <span className="truncate text-xs font-medium">{exerciseName(ex, language)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-xs px-1.5 py-0.5 rounded-full capitalize" style={{ backgroundColor: `${MUSCLE_COLORS[exTarget.toLowerCase()] || '#6b7280'}33`, color: MUSCLE_COLORS[exTarget.toLowerCase()] || '#9ca3af' }}>
                          {exTarget}
                        </span>
                        <span className="text-xs text-foreground/40 font-mono">{ex.sets?.length || 0}s</span>
                        {(() => {
                          const allPrTypes = [...new Set(ex.sets?.flatMap(s => s.pr_types || (s.is_pr ? ['weight'] : [])) || [])];
                          return allPrTypes.length > 0 ? allPrTypes.map(type => (
                            <Trophy key={type} className={`h-3 w-3 ${PR_COLORS[type] || 'text-yellow-400'}`} />
                          )) : null;
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex justify-center">
                <button className="flex items-center gap-1.5 text-xs text-foreground/70 bg-muted/50 hover:bg-muted/70 backdrop-blur-sm rounded-full px-4 py-1.5 transition-colors" onClick={(e) => { e.stopPropagation(); postDialog.open(post.id); }}>
                  <Eye className="h-3 w-3" /> {t('common.details')}
                </button>
              </div>
            </div>
          )}

          {/* ── SLIDE 3: MUSCLE ANALYSIS ── (only for posts WITH photo; no-photo has this merged in slide 1) */}
          {hasPhoto && hasExercises && (
            <div className="min-w-full bg-card dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-[#0a1a1a] p-3 pb-7 text-foreground flex flex-col h-full overflow-hidden">
              {Object.keys(postMuscleStats).length > 0 && (
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex items-center gap-2 shrink-0">
                    <Flame className="h-3.5 w-3.5 text-primary" />
                    <h4 className="font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('social.muscleAnalysis')}</h4>
                  </div>
                  <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden">
                    <MuscleBodyMap muscleStats={postMuscleStats} size="sm" interactive hideExtras colorMode="daily"
                      selectedMuscle={selectedMapMuscle}
                      onSelect={(m) => setSelectedMapMuscle(prev => prev === m ? null : m)}
                    />
                  </div>
                </div>
              )}
              {muscleGroupSplit.length > 0 && (
                <div className={`shrink-0 ${Object.keys(postMuscleStats).length > 0 ? 'border-t border-border pt-1.5' : ''}`}>
                  <h4 className="font-bold text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('social.muscleSplit')}</h4>
                  <div className={`grid gap-2 ${muscleGroupSplit.length <= 3 ? 'grid-cols-3' : muscleGroupSplit.length === 4 ? 'grid-cols-4' : 'grid-cols-5'}`}>
                    {muscleGroupSplit.map(g => (
                      <div key={g.name} className="flex flex-col items-center gap-0.5 rounded p-0.5">
                        <span className="text-xs truncate w-full text-center text-muted-foreground">{g.name}</span>
                        <div className="w-full h-1.5 rounded-full bg-muted/50 overflow-hidden my-0.5">
                          <div className="h-full rounded-full transition-all" style={{ width: `${g.pct}%`, backgroundColor: MUSCLE_GROUP_COLORS[g.name] || '#6b7280' }} />
                        </div>
                        <span className="text-xs font-bold text-foreground/80">{Math.round(g.count)} {t('workout.sets')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Carousel Controls */}
        {totalSlides > 1 && (
          <>
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
              {Array.from({ length: totalSlides }).map((_, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setCarouselSlide(i); }}
                  className={`w-2 h-2 rounded-full transition-all ${i === carouselSlide ? 'bg-primary w-4' : 'bg-foreground/30'}`} />
              ))}
            </div>
            {carouselSlide > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={(e) => { e.stopPropagation(); setCarouselSlide(s => s - 1); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 md:w-8 md:h-8 rounded-full bg-muted/70 flex items-center justify-center text-foreground hover:bg-muted active:bg-muted">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('tooltips.carouselPrevious')}</TooltipContent>
              </Tooltip>
            )}
            {carouselSlide < totalSlides - 1 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={(e) => { e.stopPropagation(); setCarouselSlide(s => s + 1); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 md:w-8 md:h-8 rounded-full bg-muted/70 flex items-center justify-center text-foreground hover:bg-muted active:bg-muted">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('tooltips.carouselNext')}</TooltipContent>
              </Tooltip>
            )}
          </>
        )}

      </div>

      <PostActions post={post} currentUser={currentUser} isLiked={isLiked} likesCount={likesCount}
        commentsCount={commentsCount} showComments={showComments} setShowComments={setShowComments} onToggleComments={handleToggleComments}
        commentText={commentText} setCommentText={setCommentText} submitting={submitting}
        handleSubmitComment={handleSubmitComment} onLike={onLike} onDeleteComment={onDeleteComment} t={t}
        setPosts={setPosts} setShowDetailModal={setShowDetailModal}
        extraAction={
          (post.exercises_detail || []).length > 0 && (
            <div className="flex items-center gap-3">
              <button onClick={openSaveToRoutine} className="flex items-center gap-2 h-9 text-muted-foreground hover:text-primary transition-colors" data-testid={`save-workout-to-routine-${post.id}`}>
                <Bookmark className="h-5 w-5" />
                <span className="text-sm font-medium">{t('common.save')}</span>
              </button>
              <button onClick={() => setShowStoryGenerator(true)} className="flex items-center gap-2 h-9 text-muted-foreground hover:text-primary transition-colors" data-testid={`share-story-${post.id}`}>
                <Share2 className="h-5 w-5" />
                <span className="text-sm font-medium">{t('social.story')}</span>
              </button>
            </div>
          )
        }
      />
    </Card>

    {/* Save to Routine Dialog */}
    <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
      <DialogContent className="max-w-[340px]" compact data-testid="social-save-routine-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Bookmark className="h-5 w-5 text-primary" /> {t('social.saveToMyRoutines')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-base md:text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('social.dayName')}</label>
            <Input value={saveDayName} onChange={e => setSaveDayName(e.target.value)} placeholder={t('social.dayNamePlaceholder')} className="mt-1" data-testid="social-save-day-name" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('social.saveTo')}</label>
            <div className="flex gap-2">
              <button onClick={() => setSaveChoice('new')}
                className={`flex-1 p-3 rounded-xl border-2 text-left transition-colors ${saveChoice === 'new' ? 'border-primary bg-primary/10' : 'border-border hover:border-border/80'}`}
                data-testid="social-save-new">
                <Plus className="h-4 w-4 text-primary mb-1" />
                <p className="text-sm font-medium">{t('social.newRoutine')}</p>
              </button>
              <button onClick={() => setSaveChoice('existing')} disabled={personalRoutines.length === 0}
                className={`flex-1 p-3 rounded-xl border-2 text-left transition-colors ${saveChoice === 'existing' ? 'border-primary bg-primary/10' : 'border-border hover:border-border/80'} ${personalRoutines.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                data-testid="social-save-existing">
                <Dumbbell className="h-4 w-4 text-primary mb-1" />
                <p className="text-sm font-medium">{t('social.existing')}</p>
                <p className="text-xs text-muted-foreground">{personalRoutines.length} {personalRoutines.length !== 1 ? t('social.routines') : t('social.routine')}</p>
              </button>
            </div>
          </div>
          {saveChoice === 'new' && (
            <div>
              <label className="text-base md:text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('social.routineName')}</label>
              <Input value={saveNewName} onChange={e => setSaveNewName(e.target.value)} placeholder={t('social.routineNamePlaceholder')} className="mt-1" data-testid="social-save-routine-name" />
            </div>
          )}
          {saveChoice === 'existing' && personalRoutines.length > 0 && (
            <div className="space-y-2">
              {personalRoutines.map(r => (
                <button key={r.id} onClick={() => setSaveTarget(r.id)}
                  className={`w-full p-3 rounded-xl border-2 text-left transition-colors ${saveTarget === r.id ? 'border-primary bg-primary/10' : 'border-border'}`}
                  data-testid={`social-save-target-${r.id}`}>
                  <p className="text-sm font-semibold">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.days?.length || 0} {(r.days?.length || 0) !== 1 ? t('social.days') : t('social.day')}</p>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSaveDialog(false)} className="flex-1">{t('common.cancel')}</Button>
          <Button onClick={handleSaveToRoutine} disabled={saving || (saveChoice === 'existing' && !saveTarget)}
            className="flex-1 bg-primary hover:bg-primary/90" data-testid="social-confirm-save-btn">
            {saving ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : <Check className="mr-2 h-4 w-4" />}
            {t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Workout Detail Modal */}
    <FullScreenPage
      open={showDetailModal}
      onClose={() => { postDialog.close(); setLightboxOpen(false); setPreviewExercise(null); setSelectedMapMuscle(null); }}
      title={displayDayName}
      subtitle={`${formatDate(post.created_at)} · ${timeAgo(post.created_at)} — ${getDisplayName(post.user_name)}`}
    >
        <div className="px-4 pb-20 space-y-3">

          {/* Photo + Stats side-by-side */}
          <div className={post.workout_photo ? 'flex gap-2 -mx-4' : ''}>
            {post.workout_photo && (
              <div
                className="cursor-pointer overflow-hidden shrink-0"
                style={{ width: '60%', aspectRatio: '9/16', maxHeight: '420px' }}
                onClick={() => setLightboxOpen(true)}
              >
                <LazyImage
                  src={post.workout_photo}
                  alt="Workout"
                  wrapperClassName="w-full h-full"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}
            <div className={`flex flex-col gap-1.5 ${post.workout_photo ? 'flex-1 px-3' : `grid gap-1.5 ${post.prs_achieved > 0 ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'}`}`}>
              <div className="text-center rounded-lg bg-neutral-200 dark:bg-neutral-700 flex-1 flex flex-col items-center justify-center">
                <p className="text-base font-bold leading-none">{post.duration_minutes}<span className="text-xs text-muted-foreground font-normal">min</span></p>
              </div>
              <div className="text-center rounded-lg bg-neutral-200 dark:bg-neutral-700 flex-1 flex flex-col items-center justify-center">
                <p className="text-base font-bold leading-none">{post.exercises_count}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('social.exercises')}</p>
              </div>
              <div className="text-center rounded-lg bg-neutral-200 dark:bg-neutral-700 flex-1 flex flex-col items-center justify-center">
                <p className="text-base font-bold leading-none">{post.sets_count}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('workout.sets')}</p>
              </div>
              <div className="text-center rounded-lg bg-neutral-200 dark:bg-neutral-700 flex-1 flex flex-col items-center justify-center">
                <p className="text-base font-bold leading-none">{post.total_volume ? `${(post.total_volume / 1000).toFixed(1)}k` : '—'}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('social.volume')}</p>
              </div>
              {post.prs_achieved > 0 && (
                <div className="text-center rounded-lg bg-neutral-200 dark:bg-neutral-700 flex-1 flex flex-col items-center justify-center">
                  <p className="text-base font-bold leading-none">🏆 {post.prs_achieved}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('social.prs')}</p>
                </div>
              )}
            </div>
          </div>
          {/* Muscle Analysis + Split */}
          {(Object.keys(postMuscleStats).length > 0 || muscleSplit.length > 0) && (
            <div>
              {Object.keys(postMuscleStats).length > 0 && (
                <div className="flex items-center justify-center">
                  <MuscleBodyMap muscleStats={postMuscleStats} size="sm" interactive hideExtras colorMode="daily"
                    selectedMuscle={selectedMapMuscle}
                    onSelect={(m) => setSelectedMapMuscle(prev => prev === m ? null : m)}
                  />
                </div>
              )}
              {muscleSplit.length > 0 && (
                <div className={Object.keys(postMuscleStats).length > 0 ? 'mt-2' : ''}>
                  <h4 className="text-xs font-semibold mb-2 uppercase tracking-wider text-muted-foreground">{t('social.muscleSplit')}</h4>
                  <div className={muscleSplit.length > 4 ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5' : 'space-y-1'}>
                    {muscleSplit.map(m => {
                      const sets = postMuscleStats[m.name] || 0;
                      const isSelected = selectedMapMuscle === splitNameToStatKey(m.name);
                      return (
                        <div key={m.name} className={`flex items-center gap-2 rounded-md px-1 py-0.5 transition-all ${isSelected ? 'ring-1 ring-foreground/50 bg-foreground/5' : ''}`}>
                          <span className={`text-xs w-16 truncate text-right capitalize transition-colors ${isSelected ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>{m.name}</span>
                          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${m.pct}%`, backgroundColor: MUSCLE_COLORS[m.name] || MUSCLE_COLORS[m.name.toLowerCase()] || '#6b7280', boxShadow: isSelected ? '0 0 6px 2px rgba(0,0,0,0.3)' : 'none' }} />
                          </div>
                          <span className={`text-xs font-bold shrink-0 w-10 text-right ${isSelected ? 'text-foreground' : 'text-foreground/70'}`}>{Math.round(sets)} {t('workout.sets')}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Exercises */}
          <div>
            <h4 className="text-xs font-semibold mb-2 uppercase tracking-wider text-muted-foreground">{t('social.allExercises')}</h4>
            <div className="space-y-2">
              {(post.exercises_detail || []).map((ex, i) => {
                const exTarget = mapMuscleTarget(ex);
                return (
                <div key={i} className="p-2.5 rounded-xl bg-neutral-200 dark:bg-neutral-700 cursor-pointer active:opacity-80" onClick={() => setPreviewExercise(ex)}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <ExerciseThumb name={ex.name} gifUrl={ex.gifUrl || ex.image_url} size={32} />
                    <p className="font-medium text-xs flex-1 min-w-0 truncate">{exerciseName(ex, language)}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded-full shrink-0 capitalize" style={{ backgroundColor: `${MUSCLE_COLORS[exTarget.toLowerCase()] || '#6b7280'}22`, color: MUSCLE_COLORS[exTarget.toLowerCase()] || '#9ca3af' }}>{exTarget}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(() => { const exMode = getExerciseInputMode(ex); return ex.sets?.map((s, sIdx) => (
                      <span key={sIdx} className={`px-1.5 py-0.5 rounded text-xs ${getSetPillClass(s)} ${getPrRingClass(s)} ${getPrBoldClass(s)}`}>
                        {getSetLabel(s)}
                        {exMode === 'cardio'
                          ? formatCardioSetSummary(s, ex)
                          : exMode === 'time'
                            ? formatDuration(s.duration_seconds)
                            : `${s.weight || 0}kg × ${s.reps || s.completed_reps || 0}`}
                        {s.rpe != null && (
                          <span className="ml-0.5 opacity-70">@{s.rpe}</span>
                        )}
                        <PrTrophies prTypes={s.pr_types} is_pr={s.is_pr} className="inline h-2.5 w-2.5 ml-0.5" />
                      </span>
                    )); })()}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Photo lightbox */}
        {lightboxOpen && post.workout_photo && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/90 z-[200]"
            onClick={() => setLightboxOpen(false)}
          >
            <img
              src={post.workout_photo}
              alt="Workout full"
              className="max-w-full max-h-full object-contain"
              onClick={e => e.stopPropagation()}
            />
            <button
              className="absolute top-3 right-3 text-white bg-black/50 rounded-full p-1"
              onClick={() => setLightboxOpen(false)}
            >
              ✕
            </button>
          </div>
        )}

    </FullScreenPage>
        <ExerciseDetailModal
          exercise={previewExercise}
          open={!!previewExercise}
          onClose={() => setPreviewExercise(null)}
        />

    {/* Story Generator */}
    <WorkoutStoryGenerator
      open={showStoryGenerator}
      onClose={() => setShowStoryGenerator(false)}
      workoutData={{
        dayName: post.day_name,
        userName: getDisplayName(post.user_name),
        duration: post.duration_minutes,
        exerciseCount: post.exercises_count,
        setsCount: post.sets_count,
        totalVolume: post.total_volume,
        exercises: post.exercises_detail || [],
        photo: post.workout_photo,
      }}
    />
    </>
  );
}

/* ═══════════════════════ Meal Post ═══════════════════════ */
function MealPost({ post, currentUser, onLike, onComment, onDeleteComment, navigate, onDeletePost, onEditPost, onReportPost, setPosts, followStatusMap, setFollowStatusMap, fetchComments }) {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showStoryGenerator, setShowStoryGenerator] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [showMealDetail, setShowMealDetail] = useState(false);
  const [carouselSlide, setCarouselSlide] = useState(0);
  const postRef = React.useRef(null);
  const commentsLoadedRef = React.useRef(false);

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoadedRef.current && fetchComments) {
      commentsLoadedRef.current = true;
      fetchComments(post.id);
    }
  };

  const isLiked = (post.likes || []).some(l => l.user_id === currentUser.id);
  const likesCount = (post.likes || []).length;
  const commentsCount = post.comment_count ?? (post.comments || []).length;
  const canDelete = post.user_id === currentUser.id || currentUser.role === 'admin';
  const { t } = useTranslation();
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('time.justNow');
    if (mins < 60) return `${mins}m ${t('time.ago')}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${t('time.ago')}`;
    const days = Math.floor(hours / 24);
    return days < 7 ? `${days}d ${t('time.ago')}` : formatDate(dateStr);
  };
  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText);
    setCommentText('');
    setSubmitting(false);
    setShowComments(true);
  };
  const handleUserClick = (userId) => {
    if (userId === currentUser.id) navigate('/my-stats');
    else navigate(`/profile/${userId}`);
  };
  const userInitialsRaw = getDisplayInitials(post.user_name);
  const userInitials = userInitialsRaw === '?' ? 'U' : userInitialsRaw;
  const hasFoods = (post.meal_foods || []).length > 0;
  const formatServing = (f) => {
    const amt = f.amount ?? f.serving_amount ?? f.quantity;
    const unit = f.unit || f.serving_unit || 'g';
    const out = formatAmountUnit(amt, unit);
    return out || null;
  };
  const hasPhoto = !!post.meal_photo;
  // If no photo: everything merged into slide 1. If photo: foods get their own slide.
  const totalSlides = hasPhoto ? (hasFoods ? 2 : 1) : 1;
  const mealSwipe = useSwipe(
    () => { if (carouselSlide < totalSlides - 1) setCarouselSlide(s => s + 1); },
    () => { if (carouselSlide > 0) setCarouselSlide(s => s - 1); }
  );

  const MEAL_ICONS = { breakfast: '🌅', lunch: '☀️', dinner: '🌙', snack: '🍎', snacks: '🍎' };

  return (
    <>
    <Card ref={postRef} className="border-border/50 overflow-hidden" data-testid={`social-post-${post.id}`}>
      <PostHeader post={post} currentUser={currentUser} canDelete={canDelete}
        onDeletePost={onDeletePost} onEditPost={onEditPost} onReportPost={onReportPost}
        handleUserClick={handleUserClick} timeAgo={timeAgo} userInitials={userInitials} t={t} followStatusMap={followStatusMap} setFollowStatusMap={setFollowStatusMap} />

      {/* Meal Carousel — slide 1: photo+overlay (or info card), slide 2: foods */}
      <div className="relative mx-4 rounded-xl overflow-hidden cursor-pointer aspect-[4/5] bg-gradient-to-br from-[#0a2a1a] to-[#1a3a2a] text-white"
        onClick={() => { if (!mealSwipe.didSwipe.current) setShowMealDetail(true); }}
        {...{ onTouchStart: mealSwipe.onTouchStart, onTouchMove: mealSwipe.onTouchMove, onTouchEnd: mealSwipe.onTouchEnd }}
        data-testid={`meal-carousel-${post.id}`}>
        <div className="flex transition-transform duration-300 ease-out h-full" style={{ transform: `translateX(-${carouselSlide * 100}%)` }}>

          {/* ── SLIDE 1 — photo + overlay OR info card ── */}
          <div className="min-w-full h-full relative">
            {hasPhoto ? (
              <>
                <img src={post.meal_photo} alt="Meal" className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-16 pb-6 px-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{MEAL_ICONS[post.meal_type] || '🍽️'}</span>
                      <h3 className="font-bold text-lg drop-shadow-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {post.meal_label || post.meal_type}
                      </h3>
                    </div>
                    <p className="text-2xl font-bold drop-shadow-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {post.meal_calories} <span className="text-sm text-white/70">kcal</span>
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <div className="text-center py-1.5 rounded-lg bg-white/10 backdrop-blur-sm">
                      <p className="text-base font-bold text-red-400">{post.meal_protein}g</p>
                      <p className="text-[10px] text-white/70 uppercase">{t('social.protein')}</p>
                    </div>
                    <div className="text-center py-1.5 rounded-lg bg-white/10 backdrop-blur-sm">
                      <p className="text-base font-bold text-amber-400">{post.meal_carbs}g</p>
                      <p className="text-[10px] text-white/70 uppercase">{t('social.carbs')}</p>
                    </div>
                    <div className="text-center py-1.5 rounded-lg bg-white/10 backdrop-blur-sm">
                      <p className="text-base font-bold text-blue-400">{post.meal_fat}g</p>
                      <p className="text-[10px] text-white/70 uppercase">{t('social.fat')}</p>
                    </div>
                  </div>
                </div>
                <button className="absolute top-3 right-3 flex items-center gap-1 text-xs text-white/80 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1"
                  onClick={(e) => { e.stopPropagation(); setShowMealDetail(true); }}>
                  <Eye className="h-3 w-3" /> {t('common.details')}
                </button>
              </>
            ) : (
              <div className="p-5 h-full flex flex-col">
                <button className="absolute top-3 right-3 flex items-center gap-1 text-xs text-white/80 bg-black/30 backdrop-blur-sm rounded-full px-2 py-1 z-10"
                  onClick={(e) => { e.stopPropagation(); setShowMealDetail(true); }}>
                  <Eye className="h-3 w-3" /> {t('common.details')}
                </button>
                <div className="flex items-center justify-between mb-3 pr-20 shrink-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-2xl">{MEAL_ICONS[post.meal_type] || '🍽️'}</span>
                    <h3 className="font-bold text-xl truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {post.meal_label || post.meal_type}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold shrink-0" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {post.meal_calories} <span className="text-sm text-white/60">kcal</span>
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2 shrink-0 mb-3">
                  <div className="text-center p-2 rounded-lg bg-white/5">
                    <Beef className="h-4 w-4 mx-auto text-red-400 mb-0.5" />
                    <p className="text-base font-bold">{post.meal_protein}g</p>
                    <p className="text-[10px] text-white/60 uppercase">{t('social.protein')}</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-white/5">
                    <Wheat className="h-4 w-4 mx-auto text-amber-400 mb-0.5" />
                    <p className="text-base font-bold">{post.meal_carbs}g</p>
                    <p className="text-[10px] text-white/60 uppercase">{t('social.carbs')}</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-white/5">
                    <Droplets className="h-4 w-4 mx-auto text-blue-400 mb-0.5" />
                    <p className="text-base font-bold">{post.meal_fat}g</p>
                    <p className="text-[10px] text-white/60 uppercase">{t('social.fat')}</p>
                  </div>
                </div>
                {hasFoods && (
                  <>
                    <div className="flex items-center gap-2 mb-2 shrink-0">
                      <Apple className="h-4 w-4 text-green-500" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-white/60">{t('social.foodItems')} ({post.meal_foods.length})</span>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-1">
                      {post.meal_foods.map((f, i) => {
                        const name = f.food_name || f.name;
                        const serving = formatServing(f);
                        return (
                          <button key={i} type="button"
                            onClick={(e) => { e.stopPropagation(); setSelectedFood(f); }}
                            className="w-full p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left"
                            data-testid={`meal-food-row-${post.id}-${i}`}>
                            <div className="flex items-baseline justify-between gap-2 mb-1">
                              <span className="font-semibold text-sm text-white truncate">{name}</span>
                              <span className="text-[11px] text-white/50 shrink-0">{f.calories} kcal</span>
                            </div>
                            <div className="flex items-center justify-between gap-2 text-[10px]">
                              {serving ? <span className="text-white/50">{serving}</span> : <span />}
                              <div className="flex items-center gap-2 text-white/60">
                                <span className="text-red-400">P{f.protein}</span>
                                <span className="text-amber-400">C{f.carbs}</span>
                                <span className="text-blue-400">F{f.fat}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── SLIDE 2 — food items (only when there's a photo) ── */}
          {hasPhoto && hasFoods && (
            <div className="min-w-full h-full p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-3 shrink-0">
                <Apple className="h-4 w-4 text-green-500" />
                <span className="text-xs font-semibold uppercase tracking-wide text-white/60">{t('social.foodItems')} ({post.meal_foods.length})</span>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-1">
                {post.meal_foods.map((f, i) => {
                  const name = f.food_name || f.name;
                  const serving = formatServing(f);
                  return (
                    <button key={i} type="button"
                      onClick={(e) => { e.stopPropagation(); setSelectedFood(f); }}
                      className="w-full p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left"
                      data-testid={`meal-food-row-${post.id}-${i}`}>
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <span className="font-semibold text-sm text-white truncate">{name}</span>
                        <span className="text-[11px] text-white/50 shrink-0">{f.calories} kcal</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-[10px]">
                        {serving ? <span className="text-white/50">{serving}</span> : <span />}
                        <div className="flex items-center gap-2 text-white/60">
                          <span className="text-red-400">P{f.protein}</span>
                          <span className="text-amber-400">C{f.carbs}</span>
                          <span className="text-blue-400">F{f.fat}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* Carousel dots */}
        {totalSlides > 1 && (
          <>
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
              {Array.from({ length: totalSlides }).map((_, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setCarouselSlide(i); }}
                  className={`h-2 rounded-full transition-all ${i === carouselSlide ? 'bg-white w-4' : 'bg-white/30 w-2'}`} />
              ))}
            </div>
            {carouselSlide > 0 && (
              <button onClick={(e) => { e.stopPropagation(); setCarouselSlide(s => s - 1); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 z-10">
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            {carouselSlide < totalSlides - 1 && (
              <button onClick={(e) => { e.stopPropagation(); setCarouselSlide(s => s + 1); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 z-10">
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </div>

      <PostActions post={post} currentUser={currentUser} isLiked={isLiked} likesCount={likesCount}
        commentsCount={commentsCount} showComments={showComments} setShowComments={setShowComments} onToggleComments={handleToggleComments}
        commentText={commentText} setCommentText={setCommentText} submitting={submitting}
        handleSubmitComment={handleSubmitComment} onLike={onLike} onDeleteComment={onDeleteComment} t={t}
        setPosts={setPosts}
        extraAction={
          <button onClick={() => setShowStoryGenerator(true)} className="flex items-center gap-2 h-9 text-muted-foreground hover:text-primary transition-colors" data-testid={`share-story-${post.id}`}>
            <Share2 className="h-5 w-5" />
            <span className="text-sm font-medium">{t('social.story')}</span>
          </button>
        }
      />
    </Card>

    <GenericPostStoryGenerator open={showStoryGenerator} onClose={() => setShowStoryGenerator(false)} post={post} postType="meal" />

    {/* Full meal details dialog — opens when clicking the meal card */}
    <Dialog open={showMealDetail} onOpenChange={setShowMealDetail}>
      <DialogContent compact className="max-w-md p-0 overflow-hidden border-white/10 bg-gradient-to-br from-[#0a2a1a] to-[#1a3a2a] text-white" data-testid={`meal-detail-dialog-${post.id}`}>
        <div className="max-h-[85dvh] overflow-y-auto">
          {post.meal_photo && (
            <img src={post.meal_photo} alt="Meal" className="w-full aspect-[4/3] object-cover" />
          )}
          <div className="p-5 space-y-4">
            <DialogHeader className="space-y-1">
              <DialogTitle className="text-white text-xl flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <span>{MEAL_ICONS[post.meal_type] || '🍽️'}</span>
                {post.meal_label || post.meal_type}
              </DialogTitle>
              <p className="text-xs text-white/50">{post.meal_date}</p>
            </DialogHeader>

            <div className="text-center p-4 rounded-xl bg-white/5">
              <p className="text-4xl font-black leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {post.meal_calories} <span className="text-base text-white/60">kcal</span>
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-3 rounded-lg bg-white/5">
                <Beef className="h-4 w-4 mx-auto text-red-400 mb-1" />
                <p className="text-lg font-bold">{post.meal_protein}g</p>
                <p className="text-[10px] text-white/60 uppercase">{t('social.protein')}</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/5">
                <Wheat className="h-4 w-4 mx-auto text-amber-400 mb-1" />
                <p className="text-lg font-bold">{post.meal_carbs}g</p>
                <p className="text-[10px] text-white/60 uppercase">{t('social.carbs')}</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/5">
                <Droplets className="h-4 w-4 mx-auto text-blue-400 mb-1" />
                <p className="text-lg font-bold">{post.meal_fat}g</p>
                <p className="text-[10px] text-white/60 uppercase">{t('social.fat')}</p>
              </div>
            </div>

            {hasFoods && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Apple className="h-4 w-4 text-green-500" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-white/60">{t('social.foodItems')} ({post.meal_foods.length})</span>
                </div>
                <div className="space-y-1.5">
                  {post.meal_foods.map((f, i) => {
                    const name = f.food_name || f.name;
                    const serving = formatServing(f);
                    return (
                      <button key={i} type="button"
                        onClick={() => { setShowMealDetail(false); setSelectedFood(f); }}
                        className="w-full p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left">
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="font-semibold text-sm text-white truncate">{name}</span>
                          <span className="text-[11px] text-white/50 shrink-0">{f.calories} kcal</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-[10px]">
                          {serving ? <span className="text-white/50">{serving}</span> : <span />}
                          <div className="flex items-center gap-2 text-white/60">
                            <span className="text-red-400">P{f.protein}</span>
                            <span className="text-amber-400">C{f.carbs}</span>
                            <span className="text-blue-400">F{f.fat}</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Per-food detail dialog — rich macro breakdown */}
    <Dialog open={!!selectedFood} onOpenChange={(o) => !o && setSelectedFood(null)}>
      <DialogContent compact className="max-w-sm p-0 overflow-hidden border-white/10 bg-gradient-to-br from-[#0a2a1a] to-[#1a3a2a] text-white" data-testid="meal-food-detail-dialog">
        {selectedFood && (() => {
          const p = Number(selectedFood.protein) || 0;
          const c = Number(selectedFood.carbs) || 0;
          const fat = Number(selectedFood.fat) || 0;
          const kcal = Number(selectedFood.calories) || 0;
          const kP = p * 4, kC = c * 4, kF = fat * 9;
          const kTotal = kP + kC + kF || 1;
          const pctP = Math.round((kP / kTotal) * 100);
          const pctC = Math.round((kC / kTotal) * 100);
          const pctF = 100 - pctP - pctC;
          const amt = Number(selectedFood.amount ?? selectedFood.serving_amount ?? selectedFood.quantity) || 0;
          const unit = selectedFood.unit || selectedFood.serving_unit || 'g';
          const per100 = amt > 0 ? {
            kcal: Math.round((kcal * 100) / amt),
            p: Math.round((p * 100) / amt),
            c: Math.round((c * 100) / amt),
            f: Math.round((fat * 100) / amt),
          } : null;
          return (
            <div className="max-h-[85dvh] overflow-y-auto p-5 space-y-4">
              <DialogHeader className="space-y-1">
                <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {selectedFood.food_name || selectedFood.name}
                </DialogTitle>
                {amt > 0 && <p className="text-xs text-white/50">{formatAmountUnit(amt, unit)} · {t('diet.perServing')}</p>}
              </DialogHeader>

              {/* Big calories */}
              <div className="text-center p-4 rounded-xl bg-white/5">
                <p className="text-5xl font-black leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {kcal}
                </p>
                <p className="text-xs text-white/60 uppercase tracking-wider mt-1">{t('social.calories')}</p>
              </div>

              {/* Macro distribution bar */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">{t('diet.nutritionFacts')}</p>
                <div className="flex h-2 rounded-full overflow-hidden bg-white/5">
                  {pctP > 0 && <div className="bg-red-400" style={{ width: `${pctP}%` }} />}
                  {pctC > 0 && <div className="bg-amber-400" style={{ width: `${pctC}%` }} />}
                  {pctF > 0 && <div className="bg-blue-400" style={{ width: `${pctF}%` }} />}
                </div>
                <div className="flex justify-between text-[10px] text-white/60 mt-1">
                  <span><span className="text-red-400">●</span> {pctP}%</span>
                  <span><span className="text-amber-400">●</span> {pctC}%</span>
                  <span><span className="text-blue-400">●</span> {pctF}%</span>
                </div>
              </div>

              {/* Macro grid */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-3 rounded-lg bg-white/5">
                  <Beef className="h-4 w-4 mx-auto text-red-400 mb-1" />
                  <p className="text-lg font-bold">{p}g</p>
                  <p className="text-[10px] text-white/60 uppercase">{t('social.protein')}</p>
                  <p className="text-[9px] text-white/40 mt-0.5">{Math.round(kP)} kcal</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-white/5">
                  <Wheat className="h-4 w-4 mx-auto text-amber-400 mb-1" />
                  <p className="text-lg font-bold">{c}g</p>
                  <p className="text-[10px] text-white/60 uppercase">{t('social.carbs')}</p>
                  <p className="text-[9px] text-white/40 mt-0.5">{Math.round(kC)} kcal</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-white/5">
                  <Droplets className="h-4 w-4 mx-auto text-blue-400 mb-1" />
                  <p className="text-lg font-bold">{fat}g</p>
                  <p className="text-[10px] text-white/60 uppercase">{t('social.fat')}</p>
                  <p className="text-[9px] text-white/40 mt-0.5">{Math.round(kF)} kcal</p>
                </div>
              </div>

              {/* Per 100g normalized */}
              {per100 && unit === 'g' && amt !== 100 && (
                <div className="p-3 rounded-lg bg-white/5">
                  <p className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Per 100g</p>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className="text-sm font-bold">{per100.kcal}</p>
                      <p className="text-[9px] text-white/40 uppercase">kcal</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-red-400">{per100.p}g</p>
                      <p className="text-[9px] text-white/40 uppercase">prot</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-amber-400">{per100.c}g</p>
                      <p className="text-[9px] text-white/40 uppercase">carb</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-blue-400">{per100.f}g</p>
                      <p className="text-[9px] text-white/40 uppercase">fat</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
    </>
  );
}

function AchievementPost({ post, currentUser, onLike, onComment, onDeleteComment, navigate, onDeletePost, onEditPost, onReportPost, setPosts, followStatusMap, setFollowStatusMap, fetchComments }) {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postRef = React.useRef(null);
  const commentsLoadedRef = React.useRef(false);
  const { t } = useTranslation();

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoadedRef.current && fetchComments) {
      commentsLoadedRef.current = true;
      fetchComments(post.id);
    }
  };

  const isLiked = (post.likes || []).some(l => l.user_id === currentUser.id);
  const likesCount = (post.likes || []).length;
  const commentsCount = post.comment_count ?? (post.comments || []).length;
  const canDelete = post.user_id === currentUser.id || currentUser.role === 'admin';

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('time.justNow');
    if (mins < 60) return `${mins}m ${t('time.ago')}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${t('time.ago')}`;
    const days = Math.floor(hours / 24);
    return days < 7 ? `${days}d ${t('time.ago')}` : formatDate(dateStr);
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText);
    setCommentText('');
    setSubmitting(false);
    setShowComments(true);
  };

  const handleUserClick = (userId) => {
    if (userId === currentUser.id) navigate('/my-stats');
    else navigate(`/profile/${userId}`);
  };

  const userInitialsRaw = getDisplayInitials(post.user_name);
  const userInitials = userInitialsRaw === '?' ? 'U' : userInitialsRaw;

  return (
    <Card ref={postRef} className="border-border/50 overflow-hidden" data-testid={`social-post-${post.id}`}>
      <PostHeader
        post={post} currentUser={currentUser} canDelete={canDelete}
        onDeletePost={onDeletePost} onEditPost={onEditPost} onReportPost={onReportPost}
        handleUserClick={handleUserClick} timeAgo={timeAgo} userInitials={userInitials} t={t}
        followStatusMap={followStatusMap} setFollowStatusMap={setFollowStatusMap}
      />

      <div
        className="relative mx-4 rounded-xl overflow-hidden aspect-[4/5] text-white"
        style={{
          background: 'linear-gradient(155deg, rgba(40,20,5,0.95) 0%, rgba(15,15,20,0.97) 100%)',
          border: '1px solid rgba(255,92,0,0.4)',
          boxShadow: '0 0 40px rgba(255,92,0,0.25)',
        }}
        data-testid={`achievement-card-${post.id}`}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 gap-3">
          <div
            className="uppercase tracking-[3px] text-[11px] font-bold"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#FF5C00' }}
          >
            {t('achievements.unlocked', 'Achievement Unlocked')}
          </div>
          <div
            className="text-[88px] leading-none my-2"
            style={{ filter: 'drop-shadow(0 0 20px rgba(255,92,0,0.6))' }}
          >
            {post.badge_icon || '🏆'}
          </div>
          <div
            className="uppercase font-extrabold text-2xl tracking-wide"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {post.badge_title}
          </div>
          <div className="text-sm text-white/75 max-w-[260px]">
            {post.badge_description}
          </div>
        </div>
      </div>

      <PostActions
        post={post} currentUser={currentUser} isLiked={isLiked} likesCount={likesCount}
        commentsCount={commentsCount} showComments={showComments} setShowComments={setShowComments}
        onToggleComments={handleToggleComments}
        commentText={commentText} setCommentText={setCommentText} submitting={submitting}
        handleSubmitComment={handleSubmitComment} onLike={onLike} onDeleteComment={onDeleteComment} t={t}
        setPosts={setPosts}
      />
    </Card>
  );
}

function PrPost({ post, currentUser, onLike, onComment, onDeleteComment, navigate, onDeletePost, onEditPost, onReportPost, setPosts, followStatusMap, setFollowStatusMap, fetchComments }) {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postRef = React.useRef(null);
  const commentsLoadedRef = React.useRef(false);
  const { t } = useTranslation();

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoadedRef.current && fetchComments) {
      commentsLoadedRef.current = true;
      fetchComments(post.id);
    }
  };

  const isLiked = (post.likes || []).some(l => l.user_id === currentUser.id);
  const likesCount = (post.likes || []).length;
  const commentsCount = post.comment_count ?? (post.comments || []).length;
  const canDelete = post.user_id === currentUser.id || currentUser.role === 'admin';

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('time.justNow');
    if (mins < 60) return `${mins}m ${t('time.ago')}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${t('time.ago')}`;
    const days = Math.floor(hours / 24);
    return days < 7 ? `${days}d ${t('time.ago')}` : formatDate(dateStr);
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText);
    setCommentText('');
    setSubmitting(false);
    setShowComments(true);
  };

  const handleUserClick = (userId) => {
    if (userId === currentUser.id) navigate('/my-stats');
    else navigate(`/profile/${userId}`);
  };

  const userInitialsRaw = getDisplayInitials(post.user_name);
  const userInitials = userInitialsRaw === '?' ? 'U' : userInitialsRaw;

  return (
    <Card ref={postRef} className="border-border/50 overflow-hidden" data-testid={`social-post-${post.id}`}>
      <PostHeader
        post={post} currentUser={currentUser} canDelete={canDelete}
        onDeletePost={onDeletePost} onEditPost={onEditPost} onReportPost={onReportPost}
        handleUserClick={handleUserClick} timeAgo={timeAgo} userInitials={userInitials} t={t}
        followStatusMap={followStatusMap} setFollowStatusMap={setFollowStatusMap}
      />

      {(() => {
        const isRepsPr = post.pr_type === 'reps' || ((post.pr_weight_kg || 0) <= 0 && (post.pr_reps || 0) > 0);
        const altText = isRepsPr
          ? `${post.pr_exercise_name} ${post.pr_reps} reps`
          : `${post.pr_exercise_name} ${post.pr_weight_kg}kg`;
        if (post.pr_image) {
          return (
            <div className="mx-4 rounded-xl overflow-hidden" data-testid={`pr-card-${post.id}`}>
              <img src={post.pr_image} alt={altText} className="w-full h-auto block" loading="lazy" />
            </div>
          );
        }
        return (
          <div
            className="relative mx-4 rounded-xl overflow-hidden aspect-[4/5] text-white"
            style={{
              background: 'linear-gradient(155deg, rgba(40,20,5,0.95) 0%, rgba(15,15,20,0.97) 100%)',
              border: '1px solid rgba(255,92,0,0.4)',
              boxShadow: '0 0 40px rgba(255,92,0,0.25)',
            }}
            data-testid={`pr-card-${post.id}`}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 gap-2">
              <div
                className="uppercase tracking-[3px] text-[11px] font-bold"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#FF5C00' }}
              >
                {t('pr.newPr', 'New PR!')}
              </div>
              <div
                className="font-extrabold"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 56, color: isRepsPr ? '#4ADE80' : '#FCD34D', lineHeight: 1 }}
              >
                {isRepsPr ? `${post.pr_reps} REPS` : `${post.pr_weight_kg} KG`}
              </div>
              {isRepsPr ? (
                <div className="text-xs text-white/60 font-semibold uppercase tracking-wide">{t('common.bodyweight', 'Bodyweight')}</div>
              ) : (post.pr_reps > 1 && (
                <div className="text-sm text-white/80 font-bold">× {post.pr_reps} REPS</div>
              ))}
              <div className="uppercase font-bold text-lg tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {post.pr_exercise_name}
              </div>
            </div>
          </div>
        );
      })()}

      <PostActions
        post={post} currentUser={currentUser} isLiked={isLiked} likesCount={likesCount}
        commentsCount={commentsCount} showComments={showComments} setShowComments={setShowComments}
        onToggleComments={handleToggleComments}
        commentText={commentText} setCommentText={setCommentText} submitting={submitting}
        handleSubmitComment={handleSubmitComment} onLike={onLike} onDeleteComment={onDeleteComment} t={t}
        setPosts={setPosts}
      />
    </Card>
  );
}

function DietDayPost({ post, currentUser, onLike, onComment, onDeleteComment, navigate, onDeletePost, onEditPost, onReportPost, setPosts, followStatusMap, setFollowStatusMap, fetchComments }) {
  const postDialog = useRoutableDialog('post');
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showStoryGenerator, setShowStoryGenerator] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const postRef = React.useRef(null);
  const commentsLoadedRef = React.useRef(false);

  useEffect(() => {
    setShowDetailModal(postDialog.id === post.id);
  }, [postDialog.id, post.id]);

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoadedRef.current && fetchComments) {
      commentsLoadedRef.current = true;
      fetchComments(post.id);
    }
  };

  const isLiked = (post.likes || []).some(l => l.user_id === currentUser.id);
  const likesCount = (post.likes || []).length;
  const commentsCount = post.comment_count ?? (post.comments || []).length;
  const canDelete = post.user_id === currentUser.id || currentUser.role === 'admin';
  const { t } = useTranslation();

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('time.justNow');
    if (mins < 60) return `${mins}m ${t('time.ago')}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${t('time.ago')}`;
    const days = Math.floor(hours / 24);
    return days < 7 ? `${days}d ${t('time.ago')}` : formatDate(dateStr);
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText);
    setCommentText('');
    setSubmitting(false);
    setShowComments(true);
  };

  const handleUserClick = (userId) => {
    if (userId === currentUser.id) navigate('/my-stats');
    else navigate(`/profile/${userId}`);
  };

  const userInitialsRaw = getDisplayInitials(post.user_name);
  const userInitials = userInitialsRaw === '?' ? 'U' : userInitialsRaw;

  const calDiff = post.day_calories - post.day_target_calories;
  const isOver = calDiff >= 200;
  const isUnder = calDiff <= -200;
  const statusLabel = isOver ? 'OVER' : isUnder ? 'UNDER' : 'ON TRACK';
  const statusColor = isOver ? 'text-red-500' : isUnder ? 'text-yellow-500' : 'text-green-500';

  const meals = post.day_meals || [];

  return (
    <>
    <Card ref={postRef} className="border-border/50 overflow-hidden" data-testid={`social-post-${post.id}`}>
      <PostHeader
        post={post} currentUser={currentUser} canDelete={canDelete}
        onDeletePost={onDeletePost} onEditPost={onEditPost}
        onReportPost={onReportPost}
        handleUserClick={handleUserClick} timeAgo={timeAgo} userInitials={userInitials} t={t}
        followStatusMap={followStatusMap} setFollowStatusMap={setFollowStatusMap}
      />

      <div
        className="relative mx-4 rounded-xl overflow-hidden cursor-pointer aspect-[4/5] bg-gradient-to-br from-[#0d1f14] to-[#1a2e1e] text-white"
        onClick={() => postDialog.open(post.id)}
        data-testid={`diet-day-card-${post.id}`}
      >
        <button
          className="absolute top-3 right-3 z-10 flex items-center gap-1 text-xs text-white/80 bg-black/30 backdrop-blur-sm rounded-full px-2 py-1"
          onClick={(e) => { e.stopPropagation(); postDialog.open(post.id); }}
        >
          <Eye className="h-3 w-3" /> {t('common.details')}
        </button>
        <div className="p-4 h-full flex flex-col gap-3">
          {/* Header row */}
          <div className="flex items-center justify-between pr-20">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xl shrink-0">🥗</span>
              <div className="min-w-0">
                <h3 className="font-bold text-base leading-tight truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {t('social.nutritionDayComplete')}
                </h3>
                <p className="text-xs text-white/50 truncate">{post.day_date}</p>
              </div>
            </div>
          </div>

          {/* Big calories + status */}
          <div className="text-center">
            <p className="text-4xl font-black leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {post.day_calories} <span className="text-base text-white/50">kcal</span>
            </p>
            <p className={`text-xs font-bold mt-1 ${statusColor}`}>{statusLabel}</p>
          </div>

          {/* Macro pills */}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center p-2 rounded-lg bg-white/5">
              <p className="text-base font-bold text-red-400">{post.day_protein}g</p>
              <p className="text-[10px] text-white/50 uppercase">{t('social.protein')}</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-white/5">
              <p className="text-base font-bold text-amber-400">{post.day_carbs}g</p>
              <p className="text-[10px] text-white/50 uppercase">{t('social.carbs')}</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-white/5">
              <p className="text-base font-bold text-blue-400">{post.day_fat}g</p>
              <p className="text-[10px] text-white/50 uppercase">{t('social.fat')}</p>
            </div>
          </div>

          {/* Compact meal summary — fills remaining space */}
          {meals.length > 0 && (
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-1.5">
              {meals.map((m, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg bg-white/5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="shrink-0">{m.meal_icon}</span>
                    <span className="text-white/90 font-semibold truncate">{m.meal_label}</span>
                    {m.foods?.length > 0 && (
                      <span className="text-xs text-white/40 shrink-0">· {m.foods.length}</span>
                    )}
                  </div>
                  <span className="text-primary font-semibold shrink-0 ml-2">{m.calories} kcal</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PostActions
        post={post} currentUser={currentUser} isLiked={isLiked} likesCount={likesCount}
        commentsCount={commentsCount} showComments={showComments} setShowComments={setShowComments} onToggleComments={handleToggleComments}
        commentText={commentText} setCommentText={setCommentText} submitting={submitting}
        handleSubmitComment={handleSubmitComment} onLike={onLike} onDeleteComment={onDeleteComment}
        t={t} setPosts={setPosts}
        extraAction={
          <button onClick={() => setShowStoryGenerator(true)} className="flex items-center gap-2 h-9 text-muted-foreground hover:text-primary transition-colors" data-testid={`share-story-${post.id}`}>
            <Share2 className="h-5 w-5" />
            <span className="text-sm font-medium">{t('social.story')}</span>
          </button>
        }
      />
    </Card>
    <GenericPostStoryGenerator open={showStoryGenerator} onClose={() => setShowStoryGenerator(false)} post={post} postType="diet_day" />
    </>
  );
}

function CheckInPost({ post, currentUser, onLike, onComment, onDeleteComment, navigate, onDeletePost, onEditPost, onReportPost, setPosts, followStatusMap, setFollowStatusMap, fetchComments }) {
  const postDialog = useRoutableDialog('post');
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [fullPhotos, setFullPhotos] = useState(null);
  const [showStoryGenerator, setShowStoryGenerator] = useState(false);
  const postRef = React.useRef(null);
  const commentsLoadedRef = React.useRef(false);

  useEffect(() => {
    const isMatch = postDialog.id === post.id;
    setShowDetailModal(isMatch);
    if (isMatch && !fullPhotos && post.has_full_photos) {
      client.get(`/social/posts/${post.id}/full-photos`)
        .then(res => setFullPhotos(res.data))
        .catch(() => {});
    }
  }, [postDialog.id, post.id, post.has_full_photos, fullPhotos]);

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoadedRef.current && fetchComments) {
      commentsLoadedRef.current = true;
      fetchComments(post.id);
    }
  };

  const isLiked = (post.likes || []).some(l => l.user_id === currentUser.id);
  const likesCount = (post.likes || []).length;
  const commentsCount = post.comment_count ?? (post.comments || []).length;
  const canDelete = post.user_id === currentUser.id || currentUser.role === 'admin';
  const { t } = useTranslation();
  const MOOD_MAP = { great: '😄', good: '🙂', neutral: '😐', bad: '😟', terrible: '😣' };

  // Build slides: each photo gets its own full-width slide
  // Order: Favourite → Relaxed (Front, Side, Back) → Flexed (Front, Side, Back)
  const slides = React.useMemo(() => {
    const src = fullPhotos || post;
    const result = [];
    if (src.checkin_photo_relaxed) result.push({ label: t('social.photoFavourite'), group: 'favourite', photos: [src.checkin_photo_relaxed] });
    if (src.checkin_photo_front) result.push({ label: t('social.photoRelaxedFront'), group: 'relaxed', photos: [src.checkin_photo_front] });
    if (src.checkin_photo_side) result.push({ label: t('social.photoRelaxedSide'), group: 'relaxed', photos: [src.checkin_photo_side] });
    if (src.checkin_photo_back) result.push({ label: t('social.photoRelaxedBack'), group: 'relaxed', photos: [src.checkin_photo_back] });
    if (src.checkin_photo_front_flex) result.push({ label: t('social.photoFlexedFront'), group: 'flexed', photos: [src.checkin_photo_front_flex] });
    if (src.checkin_photo_side_flex) result.push({ label: t('social.photoFlexedSide'), group: 'flexed', photos: [src.checkin_photo_side_flex] });
    if (src.checkin_photo_back_flex) result.push({ label: t('social.photoFlexedBack'), group: 'flexed', photos: [src.checkin_photo_back_flex] });
    // Fallback: flat array from older posts
    if (result.length === 0) {
      const flat = (post.checkin_photos || []).filter(Boolean);
      flat.forEach((photo, i) => {
        result.push({ label: `Photo ${i + 1}`, group: 'photos', photos: [photo] });
      });
    }
    return result;
  }, [post, fullPhotos]);

  const hasPhotos = slides.length > 0;

  // Full-res photos fetch is handled by the postDialog.id useEffect above.
  const handleOpenDetail = () => {
    postDialog.open(post.id);
  };

  const checkinSwipe = useSwipe(
    () => { if (activePhoto < slides.length - 1) setActivePhoto(s => s + 1); },
    () => { if (activePhoto > 0) setActivePhoto(s => s - 1); }
  );

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('time.justNow');
    if (mins < 60) return `${mins}m ${t('time.ago')}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${t('time.ago')}`;
    const days = Math.floor(hours / 24);
    return days < 7 ? `${days}d ${t('time.ago')}` : formatDate(dateStr);
  };
  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(post.id, commentText);
    setCommentText('');
    setSubmitting(false);
    setShowComments(true);
  };
  const handleUserClick = (userId) => {
    if (userId === currentUser.id) navigate('/my-stats');
    else navigate(`/profile/${userId}`);
  };
  const userInitialsRaw = getDisplayInitials(post.user_name);
  const userInitials = userInitialsRaw === '?' ? 'U' : userInitialsRaw;

  return (
    <>
    <Card ref={postRef} className="border-border/50 overflow-hidden" data-testid={`social-post-${post.id}`}>
      <PostHeader post={post} currentUser={currentUser} canDelete={canDelete}
        onDeletePost={onDeletePost} onEditPost={onEditPost} onReportPost={onReportPost}
        handleUserClick={handleUserClick} timeAgo={timeAgo} userInitials={userInitials} t={t} followStatusMap={followStatusMap} setFollowStatusMap={setFollowStatusMap} />

      <div className="mx-4 rounded-xl overflow-hidden cursor-pointer"
        onClick={() => { if (!checkinSwipe.didSwipe.current) handleOpenDetail(); }}
        data-testid={`checkin-card-${post.id}`}>
        <div className="bg-gradient-to-br from-[#1a0a2e] to-[#2a1a3e] text-white">
          {/* Swipeable Photo Slides */}
          {hasPhotos && (
            <div className="relative"
              onTouchStart={checkinSwipe.onTouchStart} onTouchMove={checkinSwipe.onTouchMove} onTouchEnd={checkinSwipe.onTouchEnd}>
              <div className="overflow-hidden">
                <div className="flex transition-transform duration-300 ease-out" style={{ transform: `translateX(-${activePhoto * 100}%)` }}>
                  {slides.map((slide, i) => (
                    <div key={i} className="min-w-full w-full">
                      {slide.photos.length === 1 ? (
                        <PinchZoomImage src={cloudinaryThumb(slide.photos[0], 500)} alt={slide.label} className="w-full aspect-[9/16] object-cover" />
                      ) : (
                        <div className="flex gap-0.5 aspect-[9/16]">
                          {slide.photos.map((photo, j) => (
                            <PinchZoomImage key={j} src={cloudinaryThumb(photo, 300)} alt={`${slide.label} ${j + 1}`} className="flex-1 h-full object-cover" />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <span className="absolute bottom-2 left-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded">{slides[activePhoto]?.label}</span>
              {slides.length > 1 && (
                <>
                  <div className="absolute bottom-2 right-2 flex gap-0.5">
                    {slides.map((_, i) => (
                      <button key={i} onClick={(e) => { e.stopPropagation(); setActivePhoto(i); }}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${i === activePhoto ? 'bg-white w-3' : 'bg-white/40'}`} />
                    ))}
                  </div>
                  {activePhoto > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button onClick={(e) => { e.stopPropagation(); setActivePhoto(s => s - 1); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60">
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t('tooltips.carouselPrevious')}</TooltipContent>
                    </Tooltip>
                  )}
                  {activePhoto < slides.length - 1 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button onClick={(e) => { e.stopPropagation(); setActivePhoto(s => s + 1); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60">
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t('tooltips.carouselNext')}</TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          )}
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-purple-400" />
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>CHECK-IN</h3>
              </div>
              {post.checkin_mood && <span className="text-2xl">{MOOD_MAP[post.checkin_mood] || post.checkin_mood}</span>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="text-center p-3 rounded-lg bg-white/5">
                <p className="text-2xl font-bold">{post.checkin_weight} <span className="text-sm text-white/60">kg</span></p>
                <p className="text-xs text-white/60 uppercase">{t('social.weight')}</p>
              </div>
              {post.checkin_body_fat && (
                <div className="text-center p-3 rounded-lg bg-white/5">
                  <p className="text-2xl font-bold">{post.checkin_body_fat}<span className="text-sm text-white/60">%</span></p>
                  <p className="text-xs text-white/60 uppercase">{t('social.bodyFat')}</p>
                </div>
              )}
            </div>
            {post.checkin_notes && <p className="text-sm text-white/60 mt-3 line-clamp-2">{post.checkin_notes}</p>}
          </div>
        </div>
      </div>

      <PostActions post={post} currentUser={currentUser} isLiked={isLiked} likesCount={likesCount}
        commentsCount={commentsCount} showComments={showComments} setShowComments={setShowComments} onToggleComments={handleToggleComments}
        commentText={commentText} setCommentText={setCommentText} submitting={submitting}
        handleSubmitComment={handleSubmitComment} onLike={onLike} onDeleteComment={onDeleteComment} t={t}
        setPosts={setPosts} setShowDetailModal={setShowDetailModal}
        extraAction={
          <button onClick={() => setShowStoryGenerator(true)} className="flex items-center gap-2 h-9 text-muted-foreground hover:text-primary transition-colors" data-testid={`share-story-${post.id}`}>
            <Share2 className="h-5 w-5" />
            <span className="text-sm font-medium">{t('social.story')}</span>
          </button>
        }
      />
    </Card>

    <GenericPostStoryGenerator open={showStoryGenerator} onClose={() => setShowStoryGenerator(false)} post={post} postType="checkin" />

    {/* Check-In Detail Modal */}
    <FullScreenPage
      open={showDetailModal}
      onClose={() => postDialog.close()}
      title={`CHECK-IN ${post.checkin_mood ? (MOOD_MAP[post.checkin_mood] || post.checkin_mood) : ''}`}
      subtitle={`${formatDate(post.created_at)} · ${timeAgo(post.created_at)} — ${getDisplayName(post.user_name)}`}
      noPadding
    >
        <div className="bg-gradient-to-br from-[#1a0a2e] to-[#2a1a3e] p-6 text-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div className="text-center p-3 rounded-lg bg-white/10">
              <p className="text-2xl font-bold">{post.checkin_weight} <span className="text-sm text-white/50">kg</span></p>
              <p className="text-xs text-white/50">{t('social.weight')}</p>
            </div>
            {post.checkin_body_fat && (
              <div className="text-center p-3 rounded-lg bg-white/10">
                <p className="text-2xl font-bold">{post.checkin_body_fat}<span className="text-sm text-white/50">%</span></p>
                <p className="text-xs text-white/50">{t('social.bodyFat')}</p>
              </div>
            )}
          </div>
        </div>
        {hasPhotos && (() => {
          const favourite = slides.filter(s => s.group === 'favourite');
          const relaxed = slides.filter(s => s.group === 'relaxed');
          const flexed = slides.filter(s => s.group === 'flexed');
          const other = slides.filter(s => !['favourite', 'relaxed', 'flexed'].includes(s.group));
          return (
            <div className="p-4 space-y-4">
              {favourite.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Favourite</p>
                  <div className="grid-cols-1 max-w-[50%] mx-auto">
                    <PinchZoomImage src={favourite[0].photos[0]} alt="Favourite" className="w-full aspect-[9/16] object-cover rounded-lg" />
                  </div>
                </div>
              )}
              {relaxed.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Relaxed</p>
                  <div className={`grid gap-2 ${relaxed.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {relaxed.map((s, j) => (
                      <PinchZoomImage key={j} src={s.photos[0]} alt={s.label} className="w-full aspect-[9/16] object-cover rounded-lg" />
                    ))}
                  </div>
                </div>
              )}
              {flexed.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Flexed</p>
                  <div className={`grid gap-2 ${flexed.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {flexed.map((s, j) => (
                      <PinchZoomImage key={j} src={s.photos[0]} alt={s.label} className="w-full aspect-[9/16] object-cover rounded-lg" />
                    ))}
                  </div>
                </div>
              )}
              {other.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Photos</p>
                  <div className={`grid gap-2 grid-cols-3`}>
                    {other.map((s, j) => (
                      <PinchZoomImage key={j} src={s.photos[0]} alt={s.label} className="w-full aspect-[9/16] object-cover rounded-lg" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {post.checkin_notes && (
          <div className="px-5 pb-5">
            <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted/50">{post.checkin_notes}</p>
          </div>
        )}
    </FullScreenPage>
    </>
  );
}


/* ═══════════════════════ Shared Components ═══════════════════════ */
function PostHeader({ post, currentUser, canDelete, onDeletePost, onEditPost, handleUserClick, timeAgo, userInitials, t, handleStartWorkout, openSaveToRoutine, onReportPost, followStatusMap, setFollowStatusMap }) {
  const navigate = useNavigate();
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(['workout', 'meal', 'checkin']);
  const [followLoading, setFollowLoading] = useState(false);

  // Lock background scroll while follow modal is open
  useEffect(() => {
    if (!showFollowModal) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouch;
    };
  }, [showFollowModal]);

  const isOwnPost = post.user_id === currentUser.id;
  const followStatus = !isOwnPost ? (followStatusMap?.[post.user_id] || null) : null;

  const doFollow = async (types) => {
    setFollowLoading(true);
    try {
      if (followStatus?.following) {
        if (types.length === 0) { await client.delete(`/follows/${post.user_id}`); }
        else { await client.patch(`/follows/${post.user_id}`, { content_types: types }); }
      } else {
        await client.post(`/follows/${post.user_id}`, { content_types: types });
      }
      const isNewFollow = !followStatus?.following && types.length > 0;
      const newStatus = types.length === 0
        ? { following: false, content_types: [] }
        : { following: true, content_types: types };
      if (setFollowStatusMap) setFollowStatusMap(prev => ({ ...prev, [post.user_id]: newStatus }));
      toast.success(followStatus?.following && types.length === 0 ? t('social.unfollowed') : followStatus?.following ? t('social.followPrefsUpdated') : t('social.nowFollowing'));
      setShowFollowModal(false);
      // Nudge new follower to enable push notifications
      if (isNewFollow) requestPushPrompt();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('social.errorActionFailed'));
    } finally {
      setFollowLoading(false);
    }
  };

  const handleFollowModalOpen = () => {
    setSelectedTypes(followStatus?.following ? (followStatus.content_types || ['workout', 'meal', 'checkin']) : ['workout', 'meal', 'checkin']);
    setShowFollowModal(true);
  };

  const toggleType = (type) =>
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);

  const CONTENT_TYPE_LABELS = { workout: t('social.filterWorkouts'), meal: t('social.filterMeals'), checkin: t('social.filterCheckins') };

  return (
    <CardHeader className="pb-3">
      <div className="flex items-start gap-3">
        <div
          className="cursor-pointer flex-shrink-0"
          onClick={() => handleUserClick(post.user_id)}
        >
          <TierAvatar
            src={post.user_picture}
            alt={post.user_name}
            fallback={userInitials}
            tier={post.user_tier || 'rookie'}
            level={post.user_level || undefined}
            size="h-10 w-10"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-semibold text-sm cursor-pointer hover:underline" onClick={() => handleUserClick(post.user_id)}>
                  {getDisplayName(post.user_name)}
                </p>
                <SubscriptionBadge badge={post.user_badge} />
                {post.username && <span className="text-xs text-muted-foreground">@{post.username}</span>}
                {post.post_type === 'meal' && <Badge className="text-xs bg-green-500/10 text-green-500 border-green-500/30 px-1.5 py-0 flex-shrink-0">{t('social.badgeMeal')}</Badge>}
                {post.post_type === 'checkin' && <Badge className="text-xs bg-purple-500/10 text-purple-500 border-purple-500/30 px-1.5 py-0 flex-shrink-0">{t('social.badgeCheckin')}</Badge>}
                {post.post_type === 'achievement' && <Badge className="text-xs bg-primary/10 text-primary border-primary/30 px-1.5 py-0 flex-shrink-0">{t('social.badgeAchievement', 'Achievement')}</Badge>}
                {post.post_type === 'pr' && <Badge className="text-xs bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/30 px-1.5 py-0 flex-shrink-0">{t('social.badgePr', 'PR')}</Badge>}
                {post.user_id !== currentUser.id && followStatus != null && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleFollowModalOpen(); }}
                        className={`flex-shrink-0 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-0.5 flex items-center justify-center rounded transition-colors active:bg-muted ${followStatus.following ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        <Bell className={`h-3.5 w-3.5 ${followStatus.following ? 'fill-primary' : ''}`} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{t('tooltips.follow')}</TooltipContent>
                  </Tooltip>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{formatDate(post.created_at)} · {timeAgo(post.created_at)}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8" data-testid={`post-menu-btn-${post.id}`} aria-label={t('tooltips.openMenu')}><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* Workout actions — available to everyone */}
                    {post.exercises_detail?.length > 0 && (
                      <>
                        <DropdownMenuItem onClick={handleStartWorkout}>
                          <Play className="h-4 w-4 mr-2 text-primary" />{t('social.startThisWorkout')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openSaveToRoutine}>
                          <Bookmark className="h-4 w-4 mr-2" />{t('social.saveThisWorkout')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {/* Other user actions */}
                    {!isOwnPost && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/chat', { state: { openChatWith: post.user_id } })}>
                          <MessageCircle className="h-4 w-4 mr-2" />{t('social.messageUser')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.preventDefault(); handleFollowModalOpen(); }}>
                          <Bell className={`h-4 w-4 mr-2 ${followStatus?.following ? 'text-primary' : ''}`} />
                          {followStatus?.following ? t('social.editFollow') : t('social.followUser')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onReportPost && onReportPost(post.id)}>
                          <Flag className="h-4 w-4 mr-2" />{t('social.reportPost')}
                        </DropdownMenuItem>
                      </>
                    )}
                    {/* Owner / admin actions */}
                    {canDelete && (
                      <>
                        {!isOwnPost ? null : <DropdownMenuSeparator />}
                        {(post.user_id === currentUser.id || currentUser.role === 'admin') && onEditPost && (
                          <DropdownMenuItem onClick={() => onEditPost(post)} data-testid={`edit-post-btn-${post.id}`}>
                            <Pencil className="h-4 w-4 mr-2" />{t('social.editPost')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDeletePost(post.id)}>
                          <Trash2 className="h-4 w-4 mr-2" />{t('social.deletePost')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
      {/* Follow content-type modal */}
      {showFollowModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 pointer-events-auto"
          style={{ touchAction: 'none' }}
          onClick={() => setShowFollowModal(false)}
          onTouchMove={(e) => e.preventDefault()}
          onWheel={(e) => e.preventDefault()}
        >
          <div className="bg-background border border-border rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl pointer-events-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {followStatus?.following ? t('social.editFollowPreferences') : t('social.followUserName', { name: getDisplayName(post.user_name) })}
              </h3>
              <button onClick={() => setShowFollowModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t('social.getNotifiedWhenPost')}</p>
            <div className="space-y-2 mb-6">
              {['workout', 'meal', 'checkin'].map(type => (
                <label key={type} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors">
                  <div
                    className="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
                    style={{
                      borderColor: selectedTypes.includes(type) ? '#FF5C00' : 'hsl(var(--border))',
                      backgroundColor: selectedTypes.includes(type) ? '#FF5C00' : 'transparent',
                    }}
                  >
                    {selectedTypes.includes(type) && <Check className="h-2.5 w-2.5 text-white" />}
                  </div>
                  <input type="checkbox" className="sr-only" checked={selectedTypes.includes(type)} onChange={() => toggleType(type)} />
                  <span className="text-sm font-medium">{CONTENT_TYPE_LABELS[type]}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              {followStatus?.following && (
                <Button variant="outline" className="flex-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={() => doFollow([])} disabled={followLoading}>
                  {t('social.unfollow')}
                </Button>
              )}
              <Button className="flex-1 bg-primary hover:bg-primary/90 text-white"
                onClick={() => doFollow(selectedTypes)} disabled={followLoading || selectedTypes.length === 0}>
                {followLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : followStatus?.following ? t('common.save') : t('social.follow')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </CardHeader>
  );
}

function PostActions({ post, currentUser, isLiked, likesCount, commentsCount, showComments, setShowComments, onToggleComments, commentText, setCommentText, submitting, handleSubmitComment, onLike, onDeleteComment, t, extraAction, setPosts, setShowDetailModal }) {
  const [replyingTo, setReplyingTo] = React.useState(null); // { commentId, userName }
  const [expandedReplies, setExpandedReplies] = React.useState(new Set());

  const handleSubmit = async () => {
    if (!commentText.trim()) return;
    const text = commentText.trim();
    setCommentText('');
    if (replyingTo) {
      try {
        const res = await client.post(`/social/posts/${post.id}/comment/${replyingTo.commentId}/reply`, { text });
        if (setPosts) setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          comments: p.comments.map(c => c.id === replyingTo.commentId
            ? { ...c, replies: [...(c.replies || []), res.data.reply] }
            : c)
        } : p));
        setExpandedReplies(prev => new Set([...prev, replyingTo.commentId]));
        setReplyingTo(null);
      } catch { toast.error(t('social.errorAddReply')); }
    } else {
      await handleSubmitComment();
    }
  };

  return (
    <>
      <div className="px-4 py-3">
        <div className="flex items-center gap-4">
          <button onClick={() => onLike(post.id)} className={`flex items-center gap-2 h-9 transition-colors ${isLiked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'}`} data-testid={`like-btn-${post.id}`}>
            <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
            <span className="text-sm font-medium">{likesCount}</span>
          </button>
          <button onClick={onToggleComments || (() => setShowComments(!showComments))} className="flex items-center gap-2 h-9 text-muted-foreground hover:text-foreground transition-colors" data-testid={`comments-toggle-${post.id}`}>
            <MessageCircle className="h-5 w-5" />
            <span className="text-sm font-medium">{commentsCount}</span>
          </button>
          {extraAction && <div className="ml-auto">{extraAction}</div>}
        </div>
        {likesCount > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            {t('social.likedBy')} <span className="font-semibold text-foreground">{getDisplayName((post.likes || [])[0]?.user_name)}</span>
            {likesCount > 1 && <> {t('common.and')} <span className="font-semibold text-foreground">{likesCount - 1} {likesCount > 2 ? t('social.others') : t('social.other')}</span></>}
          </p>
        )}
      </div>

      {/* Comment preview — always visible when there are comments */}
      {!showComments && (post.comments || []).length > 0 && (() => {
        const firstComment = post.comments[0];
        const firstReply = firstComment?.replies?.[0];
        return (
          <div className="px-4 pb-3">
            <div className="space-y-2">
              <CommentRow
                c={firstComment}
                postId={post.id}
                currentUser={currentUser}
                onLike={async () => {
                  try {
                    const res = await client.post(`/social/posts/${post.id}/comment/${firstComment.id}/like`);
                    if (setPosts) setPosts(prev => prev.map(p => p.id === post.id
                      ? { ...p, comments: p.comments.map(cm => cm.id === firstComment.id ? { ...cm, likes: res.data.liked ? [...(cm.likes || []), currentUser.id] : (cm.likes || []).filter(id => id !== currentUser.id) } : cm) }
                      : p
                    ));
                  } catch { toast.error(t('social.errorLikeComment')); }
                }}
                onEdit={async (text) => {
                  try {
                    const res = await client.patch(`/social/posts/${post.id}/comment/${firstComment.id}`, { text });
                    if (setPosts) setPosts(prev => prev.map(p => p.id === post.id
                      ? { ...p, comments: p.comments.map(cm => cm.id === firstComment.id ? { ...cm, text: res.data.comment.text, edited: true, edited_at: res.data.comment.edited_at } : cm) }
                      : p
                    ));
                  } catch { toast.error(t('social.errorEditComment')); }
                }}
                onDelete={() => onDeleteComment(post.id, firstComment.id)}
                onReply={() => { setShowComments(true); setReplyingTo({ commentId: firstComment.id, userName: firstComment.user_name }); }}
              />
              {firstReply && (
                <CommentRow
                  c={firstReply}
                  postId={post.id}
                  currentUser={currentUser}
                  isReply
                  onLike={async () => {
                    try {
                      const res = await client.post(`/social/posts/${post.id}/comment/${firstComment.id}/reply/${firstReply.id}/like`);
                      if (setPosts) setPosts(prev => prev.map(p => p.id === post.id ? {
                        ...p,
                        comments: p.comments.map(cm => cm.id === firstComment.id ? {
                          ...cm,
                          replies: (cm.replies || []).map(rp => rp.id === firstReply.id
                            ? { ...rp, likes: res.data.liked ? [...(rp.likes || []), currentUser.id] : (rp.likes || []).filter(id => id !== currentUser.id) }
                            : rp)
                        } : cm)
                      } : p));
                    } catch { toast.error(t('social.errorLikeReply')); }
                  }}
                  onEdit={async (text) => {
                    try {
                      const res = await client.patch(`/social/posts/${post.id}/comment/${firstComment.id}/reply/${firstReply.id}`, { text });
                      if (setPosts) setPosts(prev => prev.map(p => p.id === post.id ? {
                        ...p,
                        comments: p.comments.map(cm => cm.id === firstComment.id ? {
                          ...cm,
                          replies: (cm.replies || []).map(rp => rp.id === firstReply.id ? { ...rp, text: res.data.reply.text, edited: true } : rp)
                        } : cm)
                      } : p));
                    } catch { toast.error(t('social.errorEditReply')); }
                  }}
                  onDelete={async () => {
                    try {
                      await client.delete(`/social/posts/${post.id}/comment/${firstComment.id}/reply/${firstReply.id}`);
                      if (setPosts) setPosts(prev => prev.map(p => p.id === post.id ? {
                        ...p,
                        comments: p.comments.map(cm => cm.id === firstComment.id
                          ? { ...cm, replies: (cm.replies || []).filter(rp => rp.id !== firstReply.id) }
                          : cm)
                      } : p));
                    } catch { toast.error(t('social.errorDeleteReply')); }
                  }}
                />
              )}
              {(post.comment_count || post.comments.length) > 1 && (
                <button onClick={onToggleComments || (() => setShowComments(true))} className="text-xs text-muted-foreground hover:text-foreground">
                  {t('social.viewAllComments', { count: post.comment_count || post.comments.length })}
                </button>
              )}
              <div className="flex gap-2 mt-1">
                <Avatar className="h-7 w-7 shrink-0">
                  {currentUser.profile_picture ? <AvatarImage src={currentUser.profile_picture} /> : (
                    <AvatarFallback className="text-base md:text-xs bg-primary/10 text-primary">{(currentUser.name || 'U')[0]}</AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1 flex gap-2">
                  <Input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                    placeholder={t('social.writeComment')} className="h-11 md:h-8 text-base md:text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
                    onClick={() => setShowComments(true)} />
                  <Button size="sm" className="h-10 md:h-8 px-3 bg-primary hover:bg-primary/90" onClick={handleSubmit}
                    disabled={submitting || !commentText.trim()}>
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showComments && (
        <div className="px-4 pb-4">
          <Separator className="mb-3" />
          {/* Task 8+9: Redesigned comment thread with likes, edit, replies */}
          {(post.comments || []).length > 0 && (
            <div className="space-y-3 mb-3 max-h-96 overflow-y-auto">
              {(post.comments || []).map(c => (
                <div key={c.id} className="space-y-2" data-testid={`comment-${c.id}`}>
                  <CommentRow
                    c={c}
                    postId={post.id}
                    currentUser={currentUser}
                    onLike={async () => {
                      try {
                        const res = await client.post(`/social/posts/${post.id}/comment/${c.id}/like`);
                        if (setPosts) setPosts(prev => prev.map(p => p.id === post.id
                          ? { ...p, comments: p.comments.map(cm => cm.id === c.id ? { ...cm, likes: res.data.liked ? [...(cm.likes || []), currentUser.id] : (cm.likes || []).filter(id => id !== currentUser.id) } : cm) }
                          : p
                        ));
                      } catch { toast.error(t('social.errorLikeComment')); }
                    }}
                    onEdit={async (text) => {
                      try {
                        const res = await client.patch(`/social/posts/${post.id}/comment/${c.id}`, { text });
                        if (setPosts) setPosts(prev => prev.map(p => p.id === post.id
                          ? { ...p, comments: p.comments.map(cm => cm.id === c.id ? { ...cm, text: res.data.comment.text, edited: true, edited_at: res.data.comment.edited_at } : cm) }
                          : p
                        ));
                      } catch { toast.error(t('social.errorEditComment')); }
                    }}
                    onDelete={() => onDeleteComment(post.id, c.id)}
                    onReply={() => setReplyingTo({ commentId: c.id, userName: c.user_name })}
                  />

                  {/* Replies */}
                  {(c.replies || []).length > 0 && (
                    <div className="space-y-2">
                      {/* Always show first reply */}
                      {(expandedReplies.has(c.id) ? c.replies : c.replies.slice(0, 1)).map(r => (
                        <CommentRow
                          key={r.id}
                          c={r}
                          postId={post.id}
                          currentUser={currentUser}
                          isReply
                          onLike={async () => {
                            try {
                              const res = await client.post(`/social/posts/${post.id}/comment/${c.id}/reply/${r.id}/like`);
                              if (setPosts) setPosts(prev => prev.map(p => p.id === post.id ? {
                                ...p,
                                comments: p.comments.map(cm => cm.id === c.id ? {
                                  ...cm,
                                  replies: (cm.replies || []).map(rp => rp.id === r.id
                                    ? { ...rp, likes: res.data.liked ? [...(rp.likes || []), currentUser.id] : (rp.likes || []).filter(id => id !== currentUser.id) }
                                    : rp)
                                } : cm)
                              } : p));
                            } catch { toast.error(t('social.errorLikeReply')); }
                          }}
                          onEdit={async (text) => {
                            try {
                              const res = await client.patch(`/social/posts/${post.id}/comment/${c.id}/reply/${r.id}`, { text });
                              if (setPosts) setPosts(prev => prev.map(p => p.id === post.id ? {
                                ...p,
                                comments: p.comments.map(cm => cm.id === c.id ? {
                                  ...cm,
                                  replies: (cm.replies || []).map(rp => rp.id === r.id ? { ...rp, text: res.data.reply.text, edited: true } : rp)
                                } : cm)
                              } : p));
                            } catch { toast.error(t('social.errorEditReply')); }
                          }}
                          onDelete={async () => {
                            try {
                              await client.delete(`/social/posts/${post.id}/comment/${c.id}/reply/${r.id}`);
                              if (setPosts) setPosts(prev => prev.map(p => p.id === post.id ? {
                                ...p,
                                comments: p.comments.map(cm => cm.id === c.id
                                  ? { ...cm, replies: (cm.replies || []).filter(rp => rp.id !== r.id) }
                                  : cm)
                              } : p));
                            } catch { toast.error(t('social.errorDeleteReply')); }
                          }}
                        />
                      ))}
                      {c.replies.length > 1 && !expandedReplies.has(c.id) && (
                        <button
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 ml-8"
                          onClick={() => setExpandedReplies(prev => new Set([...prev, c.id]))}
                        >
                          ↳ {t('social.viewMoreReplies', { count: c.replies.length - 1 })}
                        </button>
                      )}
                      {c.replies.length > 1 && expandedReplies.has(c.id) && (
                        <button
                          className="text-xs text-muted-foreground hover:text-foreground ml-8"
                          onClick={() => setExpandedReplies(prev => { const s = new Set(prev); s.delete(c.id); return s; })}
                        >
                          {t('social.hideReplies')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Task 9c: Reply mode indicator */}
          {replyingTo && (
            <div className="flex items-center justify-between px-0 py-1.5 mb-1 bg-muted/40 text-xs text-muted-foreground rounded border border-border">
              <span className="pl-2">{t('social.replyingTo')} <span className="font-semibold text-foreground">@{getDisplayName(replyingTo.userName)}</span></span>
              <button onClick={() => setReplyingTo(null)} className="hover:text-foreground pr-2">✕</button>
            </div>
          )}

          <div className="flex gap-2">
            <Avatar className="h-7 w-7 shrink-0">
              {currentUser.profile_picture ? <AvatarImage src={currentUser.profile_picture} /> : (
                <AvatarFallback className="text-base md:text-xs bg-primary/10 text-primary">{(currentUser.name || 'U')[0]}</AvatarFallback>
              )}
            </Avatar>
            <div className="flex-1 flex gap-2">
              <Input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                placeholder={replyingTo ? `Reply to @${getDisplayName(replyingTo.userName)}…` : t('social.writeComment')} className="h-11 md:h-8 text-base md:text-sm"
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
                data-testid={`comment-input-${post.id}`} />
              <Button size="sm" className="h-10 md:h-8 px-3 bg-primary hover:bg-primary/90" onClick={handleSubmit}
                disabled={submitting || !commentText.trim()} data-testid={`submit-comment-${post.id}`}>
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
